import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { lumi, isDemoMode } from '@/lib/lumi';
import { createCurriculoStore, emptyCurriculo } from '@/lib/curriculoStore';
import CandidaturaCurriculo from '@/components/CandidaturaCurriculo';
import toast from 'react-hot-toast';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import {Camera, MapPin, Edit3, Save, X as XIcon, ChevronDown, Flame, Wine, UtensilsCrossed, GlassWater, SprayCan, UserRound, Briefcase, GraduationCap, Languages, Award, Plus, Trash2, Calendar, FileText, User, Phone, Mail, Globe, CheckCircle2, Target, BadgeCheck, Clock, Star, Car, Info, Link as LinkIcon, Package, Image, Euro, MessageSquare, Search, Users, Rss,  UserCheck, Heart, Send, Bell, Gift, Copy,  Download} from 'lucide-react';

// Imagem dedicada para o Story do Instagram (fundo pronto para overlay do codigo)
const REFERRAL_STORY_BG = 'https://static.lumi.new/e2/e2516810f27ee30b3d15a473aaa91f2d.png';

// Gera um codigo de indicacao unico e legivel (ex: EW7K9QX2) - fallback para perfis antigos
function gerarReferralCodePerfil(seed?: string): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'EW';
  const base = (seed || '') + Date.now().toString() + Math.random().toString();
  let hash = 0;
  for (let i = 0; i < base.length; i++) {
    hash = hash * 31 + base.charCodeAt(i) >>> 0;
  }
  for (let i = 0; i < 6; i++) {
    code += chars[hash % chars.length];
    hash = Math.floor(hash / chars.length) + (i + 1) * 97;
  }
  return code;
}

// ─── Social Icons ─────────────────────────────────────────────────────────────
function InstagramIcon({ size = 20 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>);

}
function TwitterIcon({ size = 20 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>);

}
function FacebookIcon({ size = 20 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>);

}
function YoutubeIcon({ size = 20 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>);

}
function LinkedinIcon({ size = 20 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>);

}

// ─── Types ────────────────────────────────────────────────────────────────────
interface SocialLinks {
  twitter: string;
  facebook: string;
  instagram: string;
  youtube: string;
  linkedin: string;
}
interface Experiencia {
  id: string;
  cargo: string;
  restaurante: string;
  cidade: string;
  dataInicio: string;
  dataFim: string;
  atual: boolean;
  descricao: string;
}
interface Formacao {
  id: string;
  curso: string;
  instituicao: string;
  ano: string;
}
interface Idioma {
  id: string;
  idioma: string;
  nivel: string;
}
interface Certificacao {
  id: string;
  nome: string;
  instituicao: string;
  ano: string;
}

type Setor = 'churrasqueiro' | 'barman' | 'cameriere' | 'sommelier' | 'limpeza' | 'recepcionista';
type Tab = 'perfil' | 'album' | 'marketplace' | 'curriculo' | 'vagas' | 'candidatos' | 'candidaturas' | 'chat' | 'amigos' | 'feed';

const AREAS_ATUACAO = [
'Barman', 'Churrasqueiro', 'Passador de Carne', 'Recepcao',
'Caixa', 'Garcom', 'Sommelier', 'Bailarino',
'Cuoco', 'Ajudante de Cuoco', 'Lava Pratos'];


const SETORES: {key: Setor;label: string;icon: React.ReactNode;}[] = [
{ key: 'churrasqueiro', label: 'Churrasqueiro', icon: <Flame size={16} /> },
{ key: 'barman', label: 'Barman', icon: <GlassWater size={16} /> },
{ key: 'cameriere', label: 'Cameriere', icon: <UtensilsCrossed size={16} /> },
{ key: 'sommelier', label: 'Sommelier', icon: <Wine size={16} /> },
{ key: 'limpeza', label: 'Limpeza', icon: <SprayCan size={16} /> },
{ key: 'recepcionista', label: 'Recepcionista', icon: <UserRound size={16} /> }];


const BANNER_IMAGES: Record<Setor, string> = {
  churrasqueiro: 'https://static.lumi.new/a5/a5b94dabfd63af6f12a5b858ed874941.jpg',
  barman: 'https://static.lumi.new/95/956c6d4c238260fa5bc32201a10454cb.jpg',
  cameriere: 'https://static.lumi.new/68/6865937459df61b418dd0181a2c65d1d.jpg',
  sommelier: 'https://static.lumi.new/bb/bbc0eac46b59fe7c39b38c4778b1c89f.jpg',
  limpeza: 'https://static.lumi.new/dd/dd84d1a94fc13fd5961d869f39aa9ff9.jpg',
  recepcionista: 'https://static.lumi.new/58/583e18c7f414e5e10477480c7fb9ca64.jpg'
};

const DOC_LABELS: Record<string, string> = {
  cidadania_europeia: 'Cidadania Europeia',
  permesso_soggiorno: 'Permesso di Soggiorno',
  visto_trabalho: 'Visto de Trabalho',
  em_processo: 'Em Processo',
  outro: 'Outro'
};

const NIVEL_COLORS: Record<string, string> = {
  Nativo: '#E53935',
  Fluente: '#E57335',
  Avancado: '#8D6E63',
  Intermediario: '#5D8AA8',
  Basico: '#666'
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function SectionHeader({
  icon, label, accentColor = '#E53935', onAdd, addLabel






}: {icon: React.ReactNode;label: string;accentColor?: string;onAdd?: () => void;addLabel?: string;}) {
  return (
    <div className="flex items-center justify-between mb-5">
      <h2 className="text-base font-bold text-white flex items-center gap-2.5">
        <div className="w-1 h-5 rounded-full" style={{ background: accentColor }} />
        {icon}
        {label}
      </h2>
      {onAdd &&
      <button
        onClick={onAdd}
        className="flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-80"
        style={{ color: accentColor }}>

          <Plus size={15} />
          {addLabel || 'Adicionar'}
        </button>
      }
    </div>);

}

function Field({ label, icon, children }: {label: string;icon?: React.ReactNode;children: React.ReactNode;}) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-gray-400 text-xs font-medium mb-1.5">
        {icon}
        {label}
      </label>
      {children}
    </div>);

}

const inputCls = 'w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-xl px-4 py-3 text-gray-300 placeholder-gray-600 focus:border-[#E53935] focus:ring-1 focus:ring-[#E53935] outline-none transition-all text-sm';
const inputSmCls = 'w-full bg-[#222] border border-[#3a3a3a] rounded-lg px-4 py-2.5 text-gray-300 placeholder-gray-600 focus:border-[#E53935] focus:ring-1 focus:ring-[#E53935] outline-none transition-all text-sm';

// ─── CV Section Divider ───────────────────────────────────────────────────────
function CvSection({ icon, title, color = '#E53935', children




}: {icon: React.ReactNode;title: string;color?: string;children: React.ReactNode;}) {
  return (
    <div className="py-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}20` }}>
          <span style={{ color }}>{icon}</span>
        </div>
        <h2 className="text-sm font-black uppercase tracking-[0.15em]" style={{ color }}>{title}</h2>
        <div className="flex-1 h-px" style={{ background: `${color}30` }} />
      </div>
      {children}
    </div>);

}

// ─── Main Component ───────────────────────────────────────────────────────────
function Perfil() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<Tab>(searchParams.get('tab') === 'curriculo' ? 'curriculo' : 'perfil');
  useEffect(() => { if (searchParams.get('tab') === 'curriculo') setActiveTab('curriculo'); }, [searchParams]);
  const cvCloud = isDemoMode || import.meta.env.VITE_PRIVATE_CV_READY === 'true';
  const [cvLoading, setCvLoading] = useState(true);
  const [cvSaving, setCvSaving] = useState(false);
  const [cvError, setCvError] = useState('');
  const [cvLoadFailed, setCvLoadFailed] = useState(false);
  const [cvReload, setCvReload] = useState(0);
  const cvSaveLock = useRef(false);

  // Pilula deslizante da barra de abas (somente icones)
  const tabBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const tabPillXRef = useRef(0);
  const [tabPill, setTabPill] = useState({ x: 0, width: 0 });
  const [tabPillDuration, setTabPillDuration] = useState(0);
  const [tabPillPronta, setTabPillPronta] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [curriculoSalvo, setCurriculoSalvo] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [profileReady, setProfileReady] = useState(false);
  const [profileLoadError, setProfileLoadError] = useState(false);
  const [profileRecordId, setProfileRecordId] = useState<string | null>(null);
  const [userProfileType, setUserProfileType] = useState<string>('trabalhador');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [albumPhotos, setAlbumPhotos] = useState<string[]>([]);
  const [albumSaving, setAlbumSaving] = useState(false);
  const [albumSaved, setAlbumSaved] = useState(false);
  const [uploadingAlbum, setUploadingAlbum] = useState(false);
  const albumInputRef = useRef<HTMLInputElement>(null);

  // Lightbox do Album
  const [fotoExpandida, setFotoExpandida] = useState<number | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Marketplace - Criar Anuncio
  const [showCriarAnuncio, setShowCriarAnuncio] = useState(false);
  const [anuncioFotos, setAnuncioFotos] = useState<string[]>([]);
  const [anuncioTitulo, setAnuncioTitulo] = useState('');
  const [anuncioDescricao, setAnuncioDescricao] = useState('');
  const [anuncioPreco, setAnuncioPreco] = useState('');
  const [anuncioCategoria, setAnuncioCategoria] = useState('');
  const [anuncioLocalizacao, setAnuncioLocalizacao] = useState('');
  const [uploadingAnuncioFoto, setUploadingAnuncioFoto] = useState(false);
  const [anuncioSaving, setAnuncioSaving] = useState(false);
  const [anuncioPublicado, setAnuncioPublicado] = useState(false);
  const [meusAnuncios, setMeusAnuncios] = useState<any[]>([]);
  const [loadingAnuncios, setLoadingAnuncios] = useState(false);
  const anuncioFotoRef = useRef<HTMLInputElement>(null);
  const [setor, setSetor] = useState<Setor>('churrasqueiro');
  const [showSetorDropdown, setShowSetorDropdown] = useState(false);
  const [customBannerUrl, setCustomBannerUrl] = useState<string>('');
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  // Referral / codigo de indicacao
  const [referralCode, setReferralCode] = useState<string>('');
  const [refCopied, setRefCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showStoryModal, setShowStoryModal] = useState(false);

  // Funcoes/areas de atuacao exibidas no perfil (badges)
  const [showFuncoesDropdown, setShowFuncoesDropdown] = useState(false);
  const [salvandoFuncoes, setSalvandoFuncoes] = useState(false);

  // ── Stripe Connect ────────────────────────────────────────────────────────
  const [stripeAccountId, setStripeAccountId] = useState<string>('');
  const [stripeData, setStripeData] = useState<any>(null);
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [creatingStripe, setCreatingStripe] = useState(false);
  const [stripeError, setStripeError] = useState('');

  const [vagasCriadas, setVagasCriadas] = useState<any[]>([]);
  const [loadingVagas, setLoadingVagas] = useState(false);

  const [chatAberto, setChatAberto] = useState<any | null>(null);
  const [chatMensagem, setChatMensagem] = useState('');
  const [mensagensChat, setMensagensChat] = useState<any[]>([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const contatoId = searchParams.get('contato');
  useEffect(() => {
    if (searchParams.get('tab') !== 'chat' || !user?.userId) return;
    setActiveTab('chat');
    if (!contatoId || contatoId === user.userId) return;
    let cancelled = false;
    setChatAberto(null);
    setMensagensChat([]);
    setChatMensagem('');
    const abrirContato = async () => {
      try {
        const { list } = await lumi.entities.user_profiles.list({ filter: { userId: contatoId } });
        if (cancelled) return;
        const contato = (list || []).find((p: any) => p.userId === contatoId);
        if (!contato) { toast.error('Usuário não encontrado.'); return; }
        setChatAberto({ candidatoId: contato.userId, candidatoNome: contato.nome || contato.userName || 'Usuário', candidatoFoto: contato.profileImageUrl || '' });
      } catch (error) {
        if (!cancelled) toast.error('Não foi possível abrir a conversa. Tente novamente.');
      }
    };
    abrirContato();
    return () => { cancelled = true; };
  }, [contatoId, searchParams, user?.userId]);

  const [curriculoModalAberto, setCurriculoModalAberto] = useState<any | null>(null);
  const [candidatosCadastrados, setCandidatosCadastrados] = useState<any[]>([]);
  const [minhasCandidaturas, setMinhasCandidaturas] = useState<any[]>([]);
  const [loadingCandidaturas, setLoadingCandidaturas] = useState(false);
  const [minhasConversas, setMinhasConversas] = useState<any[]>([]);
  const [loadingConversas, setLoadingConversas] = useState(false);

  // Amigos & Feed
  const [amigos, setAmigos] = useState<any[]>([]);
  const [solicitacoesPendentes, setSolicitacoesPendentes] = useState<any[]>([]);
  const [loadingAmigos, setLoadingAmigos] = useState(false);
  const [feedPosts, setFeedPosts] = useState<any[]>([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [novoPostTexto, setNovoPostTexto] = useState('');
  const [novoPostImagens, setNovoPostImagens] = useState<string[]>([]);
  const [uploadingPostFoto, setUploadingPostFoto] = useState(false);
  const [publicandoPost, setPublicandoPost] = useState(false);
  const postFotoRef = useRef<HTMLInputElement>(null);

  const mockupCandidatos = [
  {
    id: 'mock-1',
    nome: 'João Silva',
    foto: 'https://static.lumi.new/58/583e18c7f414e5e10477480c7fb9ca64.jpg',
    banner: 'https://static.lumi.new/bb/bbc0eac46b59fe7c39b38c4778b1c89f.jpg',
    cargoCurriculo: 'Garçom Pleno',
    vagaCandidatada: 'Barman',
    cidade: 'Milão, Itália'
  },
  {
    id: 'mock-2',
    nome: 'Maria Souza',
    foto: 'https://static.lumi.new/68/6865937459df61b418dd0181a2c65d1d.jpg',
    banner: 'https://static.lumi.new/a5/a5b94dabfd63af6f12a5b858ed874941.jpg',
    cargoCurriculo: 'Chef de Partie',
    vagaCandidatada: 'Cuoco',
    cidade: 'Milão, Itália'
  }];


  const loadMinhasVagas = async () => {
    if (!user?.userId) return;
    setLoadingVagas(true);
    try {
      const { list } = await lumi.entities.vagas.list({
        sort: { createdAt: -1 }
      });
      console.log("Minhas vagas list:", list);
      const userVagas = list?.filter((v: any) => v.userId === user.userId) || [];
      setVagasCriadas(userVagas);
    } catch (error) {
      console.error('Erro ao carregar vagas:', error);
    } finally {
      setLoadingVagas(false);
    }
  };

  const carregarMensagensChat = async (candidatoId: string) => {
    if (!user?.userId || !candidatoId) return;
    setLoadingChat(true);
    try {
      const conversaId = [user.userId, candidatoId].sort().join('_');
      const { list } = await lumi.entities.mensagens_chat.list({
        filter: { conversaId },
        sort: { dataEnvio: 1 }
      });
      setMensagensChat(list || []);

      // Marcar como lida
      const mensagensNaoLidas = (list || []).filter((m: any) => m.destinatarioId === user.userId && !m.lida);
      for (const msg of mensagensNaoLidas) {
        await lumi.entities.mensagens_chat.update(msg._id, { lida: true });
      }
    } catch (e) {
      console.error("Erro ao carregar chat:", e);
    } finally {
      setLoadingChat(false);
    }
  };

  const enviarMensagemChat = async () => {
    if (!chatMensagem.trim() || !user?.userId || !chatAberto) return;

    const texto = chatMensagem.trim();
    setChatMensagem('');

    try {
      const outroId = chatAberto.candidatoId || chatAberto.outroUsuarioId;
      if (!outroId) {
        console.error('Destinatário do chat não definido');
        return;
      }
      const conversaId = [user.userId, outroId].sort().join('_');
      const novaMsg = {
        conversaId,
        remetenteId: user.userId,
        remetenteNome: user.userName || user.email || 'Usuário',
        destinatarioId: outroId,
        destinatarioNome: chatAberto.candidatoNome || 'Usuário',
        texto,
        dataEnvio: new Date().toISOString(),
        lida: false,
        creator: user.userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Optimistic update
      setMensagensChat((prev) => [...prev, { ...novaMsg, _id: Date.now().toString(), mock: true }]);

      await lumi.entities.mensagens_chat.create(novaMsg);

      // Reload para garantir id correto e sync
      await carregarMensagensChat(outroId);
    } catch (e) {
      console.error("Erro ao enviar mensagem:", e);
    }
  };

  useEffect(() => {
    if (chatAberto) {
      const outroId = chatAberto.candidatoId || chatAberto.outroUsuarioId;
      if (!outroId) return;
      carregarMensagensChat(outroId);

      // Polling a cada 5 segundos
      const interval = setInterval(() => {
        carregarMensagensChat(outroId);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [chatAberto, user?.userId]);

  // ─── AMIGOS ───────────────────────────────────────────────────────────────
  const loadAmigos = async () => {
    if (!user?.userId) return;
    setLoadingAmigos(true);
    try {
      const { list } = await lumi.entities.amizades.list({
        sort: { createdAt: -1 }
      });
      const todas = list || [];
      // Amizades aceitas que envolvem o usuario
      const aceitas = todas.filter((a: any) =>
      a.status === 'aceito' && (
      a.solicitanteId === user.userId || a.destinatarioId === user.userId)
      );
      // Mapear para o "outro" usuario da amizade
      const amigosList = aceitas.map((a: any) => {
        const souSolicitante = a.solicitanteId === user.userId;
        return {
          amizadeId: a._id,
          usuarioId: souSolicitante ? a.destinatarioId : a.solicitanteId,
          nome: souSolicitante ? a.destinatarioNome : a.solicitanteNome,
          foto: souSolicitante ? a.destinatarioFoto : a.solicitanteFoto,
          banner: souSolicitante ? a.destinatarioBanner : a.solicitanteBanner
        };
      });
      // Enriquece registros antigos que não guardavam o nome do amigo.
      const idsAmigos = amigosList.map((a: any) => a.usuarioId).filter(Boolean);
      if (idsAmigos.length) {
        try {
          const { list: perfis } = await lumi.entities.user_profiles.list({ filter: { userId: { $in: idsAmigos } } });
          for (const amigo of amigosList) {
            const perfil = (perfis || []).find((p: any) => p.userId === amigo.usuarioId);
            if (perfil) {
              amigo.nome = perfil.nome || perfil.userName || amigo.nome;
              amigo.foto = perfil.profileImageUrl || amigo.foto;
              amigo.banner = perfil.bannerUrl || amigo.banner;
            }
          }
        } catch (error) { console.warn('Não foi possível atualizar os nomes dos amigos:', error); }
      }
      setAmigos(amigosList);
      // Solicitacoes pendentes recebidas
      const pendentes = todas.filter((a: any) =>
      a.status === 'pendente' && a.destinatarioId === user.userId
      );
      setSolicitacoesPendentes(pendentes);
    } catch (e) {
      console.error('Erro ao carregar amigos:', e);
    } finally {
      setLoadingAmigos(false);
    }
  };

  const aceitarAmizade = async (amizadeId: string) => {
    try {
      await lumi.entities.amizades.update(amizadeId, {
        status: 'aceito',
        updatedAt: new Date().toISOString()
      });
      await loadAmigos();
    } catch (e) {
      console.error('Erro ao aceitar amizade:', e);
    }
  };

  const recusarAmizade = async (amizadeId: string) => {
    try {
      await lumi.entities.amizades.delete(amizadeId);
      await loadAmigos();
    } catch (e) {
      console.error('Erro ao recusar amizade:', e);
    }
  };

  const removerAmizade = async (amizadeId: string) => {
    try {
      await lumi.entities.amizades.delete(amizadeId);
      await loadAmigos();
    } catch (e) {
      console.error('Erro ao remover amizade:', e);
    }
  };

  // ─── FEED ─────────────────────────────────────────────────────────────────
  const loadFeed = async () => {
    if (!user?.userId) return;
    setLoadingFeed(true);
    try {
      // Carregar amizades para descobrir os IDs dos amigos
      const { list: amizadesList } = await lumi.entities.amizades.list({
        sort: { createdAt: -1 }
      });
      const aceitas = (amizadesList || []).filter((a: any) =>
      a.status === 'aceito' && (
      a.solicitanteId === user.userId || a.destinatarioId === user.userId)
      );
      const amigosIds = aceitas.map((a: any) =>
      a.solicitanteId === user.userId ? a.destinatarioId : a.solicitanteId
      );
      // Inclui o proprio usuario no feed
      const idsRelevantes = new Set([...amigosIds, user.userId]);
      const { list: posts } = await lumi.entities.posts_feed.list({
        sort: { createdAt: -1 }
      });
      const postsAmigos = (posts || []).filter((p: any) => idsRelevantes.has(p.userId));
      setFeedPosts(postsAmigos);
    } catch (e) {
      console.error('Erro ao carregar feed:', e);
    } finally {
      setLoadingFeed(false);
    }
  };

  const handlePostFotoAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !user?.userId) return;
    const filesToProcess = Array.from(files).slice(0, 4 - novoPostImagens.length);
    if (filesToProcess.length === 0) return;
    setUploadingPostFoto(true);
    try {
      const results = await lumi.tools.file.upload(filesToProcess);
      const newUrls = results.filter((r: any) => r?.fileUrl).map((r: any) => r.fileUrl);
      if (newUrls.length > 0) {
        setNovoPostImagens((prev) => [...prev, ...newUrls].slice(0, 4));
      }
    } catch (err) {
      console.error('Erro ao subir foto do post:', err);
    } finally {
      setUploadingPostFoto(false);
    }
    e.target.value = '';
  };

  const publicarPost = async () => {
    if (!novoPostTexto.trim() && novoPostImagens.length === 0) return;
    if (!user?.userId) return;
    setPublicandoPost(true);
    try {
      const now = new Date().toISOString();
      await lumi.entities.posts_feed.create({
        userId: user.userId,
        userNome: displayName,
        userFoto: profileImage || '',
        conteudo: novoPostTexto.trim(),
        imagens: novoPostImagens,
        curtidas: [],
        creator: user.userId,
        createdAt: now,
        updatedAt: now
      });
      setNovoPostTexto('');
      setNovoPostImagens([]);
      await loadFeed();
    } catch (e) {
      console.error('Erro ao publicar post:', e);
    } finally {
      setPublicandoPost(false);
    }
  };

  const curtirPost = async (post: any) => {
    if (!user?.userId) return;
    try {
      const curtidas = Array.isArray(post.curtidas) ? post.curtidas : [];
      const jaCurtiu = curtidas.includes(user.userId);
      const novasCurtidas = jaCurtiu ?
      curtidas.filter((id: string) => id !== user.userId) :
      [...curtidas, user.userId];
      // Optimistic update
      setFeedPosts((prev) => prev.map((p) => p._id === post._id ? { ...p, curtidas: novasCurtidas } : p));
      await lumi.entities.posts_feed.update(post._id, {
        curtidas: novasCurtidas,
        updatedAt: new Date().toISOString()
      });
    } catch (e) {
      console.error('Erro ao curtir post:', e);
    }
  };

  const deletarPost = async (postId: string) => {
    try {
      await lumi.entities.posts_feed.delete(postId);
      await loadFeed();
    } catch (e) {
      console.error('Erro ao deletar post:', e);
    }
  };

  const loadCandidatos = async () => {
    if (!user?.userId) return;
    try {
      const { list } = await lumi.entities.candidaturas.list({
        filter: { estabelecimentoId: user.userId },
        sort: { dataCandidatura: -1 }
      });
      if (list) {
        setCandidatosCadastrados(list);
      }
    } catch (e) {
      console.error("Erro ao carregar candidatos:", e);
    }
  };

  const loadMinhasCandidaturas = async () => {
    if (!user?.userId) return;
    setLoadingCandidaturas(true);
    try {
      const { list } = await lumi.entities.candidaturas.list({
        filter: { candidatoId: user.userId },
        sort: { dataCandidatura: -1 }
      });
      setMinhasCandidaturas(list || []);
    } catch (e) {
      console.error("Erro ao carregar minhas candidaturas:", e);
    } finally {
      setLoadingCandidaturas(false);
    }
  };

  const loadMinhasConversas = async () => {
    if (!user?.userId) return;
    setLoadingConversas(true);
    try {
      const { list } = await lumi.entities.mensagens_chat.list({
        sort: { dataEnvio: -1 }
      });
      // Filtra mensagens que envolvem o usuário logado
      const minhasMsgs = (list || []).filter((m: any) =>
      m.remetenteId === user.userId || m.destinatarioId === user.userId
      );
      // Agrupa por conversaId e pega a última mensagem de cada conversa
      const conversasMap = new Map<string, any>();
      for (const msg of minhasMsgs) {
        if (!msg.conversaId) continue;
        if (!conversasMap.has(msg.conversaId)) {
          const outroUsuarioId = msg.remetenteId === user.userId ? msg.destinatarioId : msg.remetenteId;
          conversasMap.set(msg.conversaId, {
            conversaId: msg.conversaId,
            outroUsuarioId,
            ultimaMensagem: msg.texto,
            dataUltimaMensagem: msg.dataEnvio,
            naoLidas: 0
          });
        }
        const conv = conversasMap.get(msg.conversaId);
        if (msg.destinatarioId === user.userId && !msg.lida) {
          conv.naoLidas = (conv.naoLidas || 0) + 1;
        }
      }
      // Carrega dados dos outros usuários
      const conversas = Array.from(conversasMap.values());
      for (const conv of conversas) {
        try {
          const { list: profiles } = await lumi.entities.user_profiles.list({
            filter: { userId: conv.outroUsuarioId },
            limit: 1
          });
          if (profiles && profiles.length > 0) {
            conv.outroNome = profiles[0].userName || profiles[0].nome || 'Usuário';
            conv.outroFoto = profiles[0].profileImageUrl || profiles[0].photoUrl || '';
            conv.outroBanner = profiles[0].banner || '';
          } else {
            conv.outroNome = 'Usuário';
            conv.outroFoto = '';
          }
        } catch (e) {
          conv.outroNome = 'Usuário';
          conv.outroFoto = '';
        }
      }
      setMinhasConversas(conversas);
    } catch (e) {
      console.error("Erro ao carregar conversas:", e);
    } finally {
      setLoadingConversas(false);
    }
  };

  useEffect(() => {
    if (user?.userId && activeTab === 'vagas') {
      loadMinhasVagas();
    }
    if (user?.userId && activeTab === 'candidatos') {
      loadCandidatos();
    }
    if (user?.userId && activeTab === 'candidaturas') {
      loadMinhasCandidaturas();
    }
    if (user?.userId && activeTab === 'chat') {
      loadMinhasConversas();
    }
    if (user?.userId && activeTab === 'amigos') {
      loadAmigos();
    }
    if (user?.userId && activeTab === 'feed') {
      loadFeed();
    }
  }, [user?.userId, activeTab]);

  const handleDeletarVaga = async (vagaId: string) => {
    if (!vagaId) return;
    try {
      await lumi.entities.vagas.delete(vagaId);
      await loadMinhasVagas();
    } catch (error) {
      console.error('Erro ao deletar vaga:', error);
    }
  };
  // Excluir conta
  const [showExcluirModal, setShowExcluirModal] = useState(false);

  // Perfil fields
  const [biografia, setBiografia] = useState('');
  const [editBio, setEditBio] = useState(biografia);
  const [socialLinks, setSocialLinks] = useState<SocialLinks>({ twitter: '', facebook: '', instagram: '', youtube: '', linkedin: '' });
  const [editSocial, setEditSocial] = useState<SocialLinks>(socialLinks);

  // Currículo começa vazio: nunca atribuir experiências fictícias ao usuário.
  const [areasAtuacao, setAreasAtuacao] = useState<string[]>(emptyCurriculo().areasAtuacao);
  const [telefone, setTelefone] = useState(emptyCurriculo().telefone);
  const [emailContato, setEmailContato] = useState(emptyCurriculo().emailContato);
  const [cidade, setCidade] = useState(emptyCurriculo().cidade);
  const [nacionalidade, setNacionalidade] = useState(emptyCurriculo().nacionalidade);
  const [documentacao, setDocumentacao] = useState(emptyCurriculo().documentacao);
  const [linkedin, setLinkedin] = useState(emptyCurriculo().linkedin);
  const [portfolio, setPortfolio] = useState(emptyCurriculo().portfolio);
  const [objetivo, setObjetivo] = useState(emptyCurriculo().objetivo);
  const [experiencias, setExperiencias] = useState<Experiencia[]>(emptyCurriculo().experiencias);
  const [formacoes, setFormacoes] = useState<Formacao[]>(emptyCurriculo().formacoes);
  const [idiomas, setIdiomas] = useState<Idioma[]>(emptyCurriculo().idiomas);
  const [habilidades, setHabilidades] = useState<string[]>(emptyCurriculo().habilidades);
  const [certificacoes, setCertificacoes] = useState<Certificacao[]>(emptyCurriculo().certificacoes);
  const [disponibilidade, setDisponibilidade] = useState(emptyCurriculo().disponibilidade);
  const [disponibilidadeViagem, setDisponibilidadeViagem] = useState(emptyCurriculo().disponibilidadeViagem);
  const [cnh, setCnh] = useState(emptyCurriculo().cnh);
  const [referencias, setReferencias] = useState(emptyCurriculo().referencias);
  const [altura, setAltura] = useState(emptyCurriculo().altura);
  const [novaHabilidade, setNovaHabilidade] = useState('');

  useEffect(() => {
    let cancelled = false;
    setCvLoading(true); setCvError(''); setCvLoadFailed(false); setCurriculoSalvo(false);
    const reset = emptyCurriculo();
    setAreasAtuacao(reset.areasAtuacao);
    setTelefone(reset.telefone);
    setEmailContato(reset.emailContato);
    setCidade(reset.cidade);
    setNacionalidade(reset.nacionalidade);
    setDocumentacao(reset.documentacao);
    setLinkedin(reset.linkedin);
    setPortfolio(reset.portfolio);
    setObjetivo(reset.objetivo);
    setExperiencias(reset.experiencias);
    setFormacoes(reset.formacoes);
    setIdiomas(reset.idiomas);
    setHabilidades(reset.habilidades);
    setCertificacoes(reset.certificacoes);
    setDisponibilidade(reset.disponibilidade);
    setDisponibilidadeViagem(reset.disponibilidadeViagem);
    setCnh(reset.cnh);
    setReferencias(reset.referencias);
    setAltura(reset.altura);
    if (!user?.userId) { setCvLoading(false); return; }
    const load = async () => {
      try {
        const store = createCurriculoStore({ cloud: cvCloud, storage: window.localStorage, entity: lumi.entities.curriculos });
        const data = await store.load(user.userId);
        if (cancelled) return;
        if (data) {
          setAreasAtuacao(data.areasAtuacao);
          setTelefone(data.telefone);
          setEmailContato(data.emailContato);
          setCidade(data.cidade);
          setNacionalidade(data.nacionalidade);
          setDocumentacao(data.documentacao);
          setLinkedin(data.linkedin);
          setPortfolio(data.portfolio);
          setObjetivo(data.objetivo);
          setExperiencias(data.experiencias);
          setFormacoes(data.formacoes);
          setIdiomas(data.idiomas);
          setHabilidades(data.habilidades);
          setCertificacoes(data.certificacoes);
          setDisponibilidade(data.disponibilidade);
          setDisponibilidadeViagem(data.disponibilidadeViagem);
          setCnh(data.cnh);
          setReferencias(data.referencias);
          setAltura(data.altura);
          setCurriculoSalvo(true);
        } else setEmailContato(user.email || '');
      } catch {
        if (!cancelled) { setCvError('Não foi possível carregar seu currículo. Tente novamente antes de editar.'); setCvLoadFailed(true); }
      } finally { if (!cancelled) setCvLoading(false); }
    };
    void load();
    return () => { cancelled = true; };
  }, [user?.userId, cvCloud, cvReload]);

  useEffect(() => {
    if (!isAuthenticated) navigate('/cadastro');
  }, [isAuthenticated, navigate]);



  useEffect(() => {
    if (user) {
      if (!emailContato && user.email) setEmailContato(user.email);
    }
  }, [user]);

  // Carregar foto de perfil e album salvos do banco
  useEffect(() => {
    if (!user?.userId) return;
    const loadProfileData = async () => {
      setProfileReady(false); setProfileLoadError(false);
      try {
        const { list } = await lumi.entities.user_profiles.list({
          filter: { userId: user.userId },
          limit: 2
        });
        if (!list?.length || (list.length === 1 && !['trabalhador', 'restaurante'].includes(list[0].profileType))) { navigate('/completar-cadastro', { replace: true }); return; }
        if (list.length !== 1) throw new Error('Perfis duplicados');
        if (list && list.length > 0) {
          const profile = list[0];
          if (profile.profileImageUrl) {
            setProfileImage(profile.profileImageUrl);
          }
          if (profile.profileType) {
            setUserProfileType(profile.profileType);
          }
          setProfileRecordId(profile._id);
          if (profile.stripeAccountId) {
            setStripeAccountId(profile.stripeAccountId);
          }
          if (profile.albumPhotos && Array.isArray(profile.albumPhotos)) {
            setAlbumPhotos(profile.albumPhotos);
          }
          if (profile.bannerUrl) {
            setCustomBannerUrl(profile.bannerUrl);
          }
          if (profile.biografia) {
            setBiografia(profile.biografia);
            setEditBio(profile.biografia);
          }
          if (profile.socialLinks && typeof profile.socialLinks === 'object') {
            const sl = {
              twitter: profile.socialLinks.twitter || '',
              facebook: profile.socialLinks.facebook || '',
              instagram: profile.socialLinks.instagram || '',
              youtube: profile.socialLinks.youtube || '',
              linkedin: profile.socialLinks.linkedin || ''
            };
            setSocialLinks(sl);
            setEditSocial(sl);
          }
          if (profile.setor) {
            setSetor(profile.setor as Setor);
          }
          if (Array.isArray(profile.areasAtuacao)) {
            setAreasAtuacao(profile.areasAtuacao);
          }
          // Referral: usa o codigo existente ou gera e persiste para perfis antigos
          if (profile.referralCode) {
            setReferralCode(profile.referralCode);
          } else {
            const novoCodigo = gerarReferralCodePerfil(user.userId);
            setReferralCode(novoCodigo);
            try {
              await lumi.entities.user_profiles.update(profile._id, {
                referralCode: novoCodigo,
                updatedAt: new Date().toISOString()
              });
            } catch (e) {
              console.error('Erro ao gerar codigo de indicacao:', e);
            }
          }
        }
        setProfileReady(true);
      } catch (error) {
        setProfileLoadError(true);
        console.error('Erro ao carregar dados do perfil:', error);
      }
    };
    loadProfileData();
    // Carregar solicitacoes pendentes para exibir badge na aba Amigos
    const loadPendentes = async () => {
      try {
        const { list } = await lumi.entities.amizades.list({ sort: { createdAt: -1 } });
        const pendentes = (list || []).filter((a: any) =>
        a.status === 'pendente' && a.destinatarioId === user.userId
        );
        setSolicitacoesPendentes(pendentes);
      } catch (e) {
        console.error('Erro ao carregar solicitacoes:', e);
      }
    };
    loadPendentes();
  }, [user?.userId]);

  const handleSave = async () => {

    // Persistir dados publicos do perfil em user_profiles
    if (!user?.userId) return;
    try {
      const now = new Date().toISOString();
      const dadosPublicos: Record<string, any> = {
        biografia: editBio,
        socialLinks: editSocial,
        setor,
        nome: displayName,
        cidade,
        updatedAt: now
      };
      if (profileRecordId) {
        await lumi.entities.user_profiles.update(profileRecordId, dadosPublicos);
      } else {
        const created = await lumi.entities.user_profiles.create({
          userId: user.userId,
          ...dadosPublicos,
          creator: user.userId,
          createdAt: now
        });
        if (created?._id) setProfileRecordId(created._id);
      }
      setBiografia(editBio); setSocialLinks(editSocial); setIsEditing(false);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      toast.error('Não foi possível salvar o perfil. Tente novamente.');
      console.error('Erro ao salvar dados do perfil:', error);
    }
  };

  const handleCancel = () => {
    setEditBio(biografia);
    setEditSocial(socialLinks);
    setIsEditing(false);
  };

  const handleBannerChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.userId) return;

    // Preview imediato
    const reader = new FileReader();
    reader.onloadend = () => setCustomBannerUrl(reader.result as string);
    reader.readAsDataURL(file);

    setUploadingBanner(true);
    try {
      const results = await lumi.tools.file.upload([file]);
      const uploaded = results?.[0];
      if (!uploaded?.fileUrl) {
        console.error('Erro no upload do banner:', uploaded?.uploadError);
        setUploadingBanner(false);
        return;
      }
      const bannerUrl = uploaded.fileUrl;
      setCustomBannerUrl(bannerUrl);

      const now = new Date().toISOString();
      if (profileRecordId) {
        await lumi.entities.user_profiles.update(profileRecordId, {
          bannerUrl,
          updatedAt: now
        });
      } else {
        const created = await lumi.entities.user_profiles.create({
          userId: user.userId,
          bannerUrl,
          creator: user.userId,
          createdAt: now,
          updatedAt: now
        });
        if (created?._id) setProfileRecordId(created._id);
      }
      console.log('Banner salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar banner:', error);
    } finally {
      setUploadingBanner(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleProfileImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.userId) return;

    // Mostrar preview imediato
    const reader = new FileReader();
    reader.onloadend = () => setProfileImage(reader.result as string);
    reader.readAsDataURL(file);

    // Upload e persistencia
    setUploadingPhoto(true);
    try {
      // 1. Upload do arquivo para storage
      const results = await lumi.tools.file.upload([file]);
      const uploaded = results?.[0];
      if (!uploaded?.fileUrl) {
        console.error('Erro no upload:', uploaded?.uploadError);
        setUploadingPhoto(false);
        return;
      }
      const imageUrl = uploaded.fileUrl;
      setProfileImage(imageUrl);

      // 2. Salvar/atualizar URL no banco
      const now = new Date().toISOString();
      if (profileRecordId) {
        await lumi.entities.user_profiles.update(profileRecordId, {
          profileImageUrl: imageUrl,
          updatedAt: now
        });
      } else {
        const created = await lumi.entities.user_profiles.create({
          userId: user.userId,
          profileImageUrl: imageUrl,
          creator: user.userId,
          createdAt: now,
          updatedAt: now
        });
        if (created?._id) setProfileRecordId(created._id);
      }
      console.log('Foto de perfil salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar foto de perfil:', error);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSalvarCurriculo = async () => {
    if (!user?.userId || cvSaveLock.current || cvLoading || cvLoadFailed) return;
    cvSaveLock.current = true; setCvSaving(true); setCvError('');
    try {
      const store = createCurriculoStore({ cloud: cvCloud, storage: window.localStorage, entity: lumi.entities.curriculos });
      await store.save(user.userId, { areasAtuacao, telefone, emailContato, cidade, nacionalidade, documentacao, linkedin, portfolio, objetivo, experiencias, formacoes, idiomas, habilidades, certificacoes, disponibilidade, disponibilidadeViagem, cnh, referencias, altura });
      setCurriculoSalvo(true);
      toast.success(isDemoMode ? 'Currículo salvo na demonstração.' : cvCloud ? 'Currículo salvo na sua conta.' : 'Currículo salvo neste navegador.');
    } catch {
      setCvError('Não foi possível salvar. Seus campos continuam na tela; tente novamente.');
    } finally { cvSaveLock.current = false; setCvSaving(false); }
  };

  // ─── Salvar Currículo em PDF (modelo fiel ao anexo) ───────────────────────────
  const handleSalvarPDF = () => {
    const escapeHtml = (str: string = '') =>
    String(str).
    replace(/&/g, '&amp;').
    replace(/</g, '&lt;').
    replace(/>/g, '&gt;').
    replace(/"/g, '&quot;').
    replace(/'/g, '&#39;');

    const fmtPeriodo = (inicio: string, fim: string, atual: boolean) => {
      const fmt = (d: string) => {
        if (!d) return '';
        const [y, m] = d.split('-');
        const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        return `${meses[parseInt(m) - 1]}/${y}`;
      };
      if (!inicio && !fim && !atual) return '';
      return `${fmt(inicio)} — ${atual ? 'Presente' : fmt(fim)}`;
    };

    const DOC_PDF_LABELS: Record<string, string> = {
      permesso_soggiorno: 'Permesso di Soggiorno',
      cidadania_europeia: 'Cidadania Italiana',
      visto_trabalho: 'Recibo Permesso',
      em_processo: 'Em Processo',
      outro: 'Outro'
    };

    // SVG icons inline (green, matching the reference image)
    const iconWhatsapp = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="14" height="14" style="flex-shrink:0"><path fill="#2d6a4f" d="M16 0C7.164 0 0 7.163 0 16c0 2.822.736 5.476 2.027 7.789L0 32l8.418-2.004A15.935 15.935 0 0016 32c8.836 0 16-7.163 16-16S24.836 0 16 0zm0 29.333a13.28 13.28 0 01-6.774-1.847l-.486-.29-5.002 1.192 1.224-4.87-.317-.5A13.249 13.249 0 012.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.28-9.907c-.398-.2-2.356-1.163-2.72-1.295-.365-.133-.63-.2-.896.2-.266.4-1.031 1.295-1.264 1.561-.232.266-.465.3-.863.1-.398-.2-1.682-.62-3.203-1.977-1.184-1.056-1.983-2.36-2.215-2.759-.232-.399-.025-.615.174-.813.179-.178.398-.465.597-.697.2-.233.266-.4.4-.665.132-.266.066-.499-.034-.698-.1-.2-.896-2.16-1.228-2.958-.322-.775-.651-.67-.896-.682l-.764-.013c-.266 0-.697.1-1.062.499-.365.399-1.395 1.363-1.395 3.322 0 1.96 1.428 3.854 1.628 4.12.2.265 2.81 4.29 6.808 6.018.951.41 1.693.655 2.272.838.954.303 1.822.26 2.509.158.765-.114 2.356-.963 2.69-1.894.332-.93.332-1.728.232-1.894-.1-.166-.365-.266-.763-.465z"/></svg>`;
    const iconPin = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#2d6a4f" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`;
    const iconGlobe = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#2d6a4f" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`;
    const iconUser = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#2d6a4f" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`;
    const iconClock = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#2d6a4f" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;
    const iconGrad = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#2d6a4f" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>`;
    const iconCal = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`;

    // Experiências
    const experienciasHtml = experiencias.map((exp) => {
      const periodo = fmtPeriodo(exp.dataInicio, exp.dataFim, exp.atual);
      return `
        <div class="exp-item">
          <div class="exp-header">
            <span class="exp-dot"></span>
            <span class="exp-position">${escapeHtml(exp.cargo || '')}</span>
          </div>
          ${periodo ? `<div class="exp-period">${iconCal} ${escapeHtml(periodo)}</div>` : ''}
          ${exp.restaurante ? `<div class="exp-company">${escapeHtml(exp.restaurante)}${exp.cidade ? ' — ' + escapeHtml(exp.cidade) : ''}</div>` : ''}
          ${exp.descricao ? `<p class="exp-desc">${escapeHtml(exp.descricao).replace(/\n/g, '<br/>')}</p>` : ''}
        </div>
        <div class="exp-divider"></div>`;
    }).join('');

    // Idiomas
    const idiomasHtml = idiomas.map((i) => `
      <div class="left-icon-row">
        ${iconGlobe}
        <div>
          <div class="left-icon-label">${escapeHtml(i.idioma || '')}</div>
          <div class="left-icon-value">${escapeHtml(i.nivel || '')}</div>
        </div>
      </div>`).join('');

    // Formação
    const formacaoHtml = formacoes.map((f) => `
      <div class="left-icon-row">
        ${iconGrad}
        <div class="left-icon-value">${escapeHtml(f.curso || 'Ensino superior')}</div>
      </div>`).join('');

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8" />
<title>Curriculo — ${escapeHtml(displayName)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { background: #fff; color: #1a1a1a; font-family: 'Helvetica Neue', Arial, sans-serif; }

  @page { size: A4 portrait; margin: 0; }
  @media print {
    html, body { background: #fff; width: 210mm; }
    .page { margin: 0; box-shadow: none; }
    .toolbar { display: none !important; }
  }

  .toolbar {
    position: fixed; top: 10px; right: 10px;
    display: flex; gap: 8px; z-index: 9999;
  }
  .toolbar button {
    background: #1a1a1a; color: #fff; border: 0;
    padding: 8px 16px; font-size: 9pt;
    border-radius: 4px; cursor: pointer;
    font-family: inherit; font-weight: 700;
  }
  .toolbar button.secondary { background: #888; }
  .toolbar button:hover { opacity: 0.85; }

  .page {
    width: 210mm;
    min-height: 297mm;
    margin: 0 auto;
    background: #fff;
    display: grid;
    grid-template-columns: 75mm 1fr;
  }

  /* LEFT COLUMN */
  .left-col {
    background: #f5f5f3;
    padding: 10mm 7mm 10mm 8mm;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  /* Square photo with green border */
  .photo-wrap {
    width: 54mm;
    height: 54mm;
    border: 3px solid #2d6a4f;
    overflow: hidden;
    background: #ddd;
    margin-bottom: 8mm;
    flex-shrink: 0;
  }
  .photo-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .photo-placeholder {
    width: 100%; height: 100%;
    display: flex; align-items: center; justify-content: center;
    font-size: 22pt; font-weight: 800; color: #fff;
    background: linear-gradient(135deg, #2d6a4f, #1b4332);
  }

  /* Section titles */
  .left-section-title {
    font-size: 9pt;
    font-weight: 900;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #1a1a1a;
    margin-bottom: 2mm;
  }
  .left-title-underline {
    width: 8mm;
    height: 1.5px;
    background: #2d6a4f;
    margin-bottom: 4mm;
  }

  /* Icon rows */
  .left-icon-row {
    display: flex;
    align-items: flex-start;
    gap: 3mm;
    margin-bottom: 3.5mm;
  }
  .left-icon-label {
    font-size: 8pt;
    font-weight: 700;
    color: #1a1a1a;
    line-height: 1.3;
  }
  .left-icon-value {
    font-size: 7.5pt;
    color: #444;
    line-height: 1.4;
  }

  /* Section separator */
  .left-divider {
    border: none;
    border-top: 0.5px solid #ccc;
    margin: 4mm 0;
  }

  .left-section { margin-bottom: 4mm; }

  /* RIGHT COLUMN */
  .right-col {
    background: #fff;
    padding: 10mm 10mm 10mm 9mm;
  }

  .name {
    font-size: 24pt;
    font-weight: 900;
    letter-spacing: 1px;
    color: #1a1a1a;
    text-transform: uppercase;
    line-height: 1.1;
    margin-bottom: 2mm;
  }
  .name-accent {
    width: 10mm;
    height: 2.5px;
    background: #2d6a4f;
    margin-bottom: 7mm;
  }

  .right-section-title {
    font-size: 10pt;
    font-weight: 900;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #1a1a1a;
    margin-bottom: 1.5mm;
  }
  .right-title-underline {
    width: 8mm;
    height: 1.5px;
    background: #2d6a4f;
    margin-bottom: 4mm;
  }
  .right-divider {
    border: none;
    border-top: 0.5px solid #ddd;
    margin: 5mm 0;
  }

  .objetivo-text {
    font-size: 8.5pt;
    color: #333;
    line-height: 1.6;
  }

  /* Experience */
  .exp-item { margin-bottom: 1mm; }
  .exp-header {
    display: flex;
    align-items: center;
    gap: 3mm;
    margin-bottom: 1.5mm;
  }
  .exp-dot {
    width: 3mm;
    height: 3mm;
    border-radius: 50%;
    background: #2d6a4f;
    flex-shrink: 0;
    display: inline-block;
  }
  .exp-position {
    font-size: 9.5pt;
    font-weight: 800;
    color: #1a1a1a;
  }
  .exp-period {
    display: flex;
    align-items: center;
    gap: 2mm;
    font-size: 8pt;
    color: #555;
    margin-bottom: 1mm;
    margin-left: 6mm;
  }
  .exp-company {
    font-size: 8pt;
    color: #555;
    font-style: italic;
    margin-bottom: 2mm;
    margin-left: 6mm;
  }
  .exp-desc {
    font-size: 8pt;
    color: #333;
    line-height: 1.55;
    margin-left: 6mm;
  }
  .exp-divider {
    border: none;
    border-top: 0.5px solid #e0e0e0;
    margin: 4mm 0;
  }
  .exp-divider:last-child { display: none; }
</style>
</head>
<body>
  <div class="toolbar">
    <button onclick="window.print()">Imprimir / Salvar PDF</button>
    <button class="secondary" onclick="window.close()">Fechar</button>
  </div>

  <div class="page">
    <!-- COLUNA ESQUERDA -->
    <aside class="left-col">
      <div class="photo-wrap">
        ${profileImage ?
    `<img src="${escapeHtml(profileImage)}" alt="Foto" />` :
    `<div class="photo-placeholder">${escapeHtml(initials)}</div>`}
      </div>

      <!-- CONTATO -->
      <div class="left-section">
        <div class="left-section-title">CONTATO</div>
        <div class="left-title-underline"></div>
        ${telefone ? `
        <div class="left-icon-row">
          ${iconWhatsapp}
          <div>
            <div class="left-icon-label">Telefone</div>
            <div class="left-icon-value">${escapeHtml(telefone)}</div>
          </div>
        </div>` : ''}
        ${cidade ? `
        <div class="left-icon-row">
          ${iconPin}
          <div>
            <div class="left-icon-label">Cidade</div>
            <div class="left-icon-value">${escapeHtml(cidade)}</div>
          </div>
        </div>` : ''}
        ${documentacao ? `
        <div class="left-icon-row">
          ${iconGlobe}
          <div>
            <div class="left-icon-label">Documentação</div>
            <div class="left-icon-value">${escapeHtml(DOC_PDF_LABELS[documentacao] || documentacao)}</div>
          </div>
        </div>` : ''}
        ${nacionalidade ? `
        <div class="left-icon-row">
          ${iconUser}
          <div>
            <div class="left-icon-label">Nacionalidade</div>
            <div class="left-icon-value">${escapeHtml(nacionalidade)}</div>
          </div>
        </div>` : ''}
      </div>

      <hr class="left-divider" />

      <!-- DISPONIBILIDADE -->
      ${disponibilidade ? `
      <div class="left-section">
        <div class="left-section-title">DISPONIBILIDADE</div>
        <div class="left-title-underline"></div>
        <div class="left-icon-row">
          ${iconClock}
          <div class="left-icon-value">${escapeHtml(disponibilidade)}</div>
        </div>
      </div>
      <hr class="left-divider" />` : ''}

      <!-- FORMAÇÃO -->
      ${formacaoHtml ? `
      <div class="left-section">
        <div class="left-section-title">FORMAÇÃO</div>
        <div class="left-title-underline"></div>
        ${formacaoHtml}
      </div>
      <hr class="left-divider" />` : ''}

      <!-- IDIOMAS -->
      ${idiomasHtml ? `
      <div class="left-section">
        <div class="left-section-title">IDIOMAS</div>
        <div class="left-title-underline"></div>
        ${idiomasHtml}
      </div>` : ''}
    </aside>

    <!-- COLUNA DIREITA -->
    <main class="right-col">
      <div class="name">${escapeHtml(displayName)}</div>
      <div class="name-accent"></div>

      <!-- OBJETIVO -->
      <div class="right-section-title">OBJETIVO</div>
      <div class="right-title-underline"></div>
      ${objetivo ? `<p class="objetivo-text">${escapeHtml(objetivo).replace(/\n/g, '<br/>')}</p>` : ''}

      <div class="right-divider"></div>

      <!-- EXPERIÊNCIAS -->
      ${experiencias.length ? `
      <div class="right-section-title">MINHAS EXPERIÊNCIAS</div>
      <div class="right-title-underline"></div>
      ${experienciasHtml}` : ''}
    </main>
  </div>

  <script>
    window.addEventListener('load', function() {
      setTimeout(function() { window.print(); }, 500);
    });
  <\/script>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=900,height=1200');
    if (!win) {
      alert('Nao foi possivel abrir a janela de impressao. Verifique o bloqueador de pop-ups do navegador.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  // Experiencias
  const addExperiencia = () => setExperiencias([...experiencias, { id: Date.now().toString(), cargo: '', restaurante: '', cidade: '', dataInicio: '', dataFim: '', atual: false, descricao: '' }]);
  const updateExperiencia = (id: string, field: keyof Experiencia, value: string | boolean) => setExperiencias(experiencias.map((e) => e.id === id ? { ...e, [field]: value } : e));
  const removeExperiencia = (id: string) => setExperiencias(experiencias.filter((e) => e.id !== id));

  // Formacoes
  const addFormacao = () => setFormacoes([...formacoes, { id: Date.now().toString(), curso: '', instituicao: '', ano: '' }]);
  const updateFormacao = (id: string, field: keyof Formacao, value: string) => setFormacoes(formacoes.map((f) => f.id === id ? { ...f, [field]: value } : f));
  const removeFormacao = (id: string) => setFormacoes(formacoes.filter((f) => f.id !== id));

  // Idiomas
  const addIdioma = () => setIdiomas([...idiomas, { id: Date.now().toString(), idioma: '', nivel: 'Basico' }]);
  const updateIdioma = (id: string, field: keyof Idioma, value: string) => setIdiomas(idiomas.map((i) => i.id === id ? { ...i, [field]: value } : i));
  const removeIdioma = (id: string) => setIdiomas(idiomas.filter((i) => i.id !== id));

  // Habilidades
  const addHabilidade = () => {
    if (novaHabilidade.trim() && !habilidades.includes(novaHabilidade.trim())) {
      setHabilidades([...habilidades, novaHabilidade.trim()]);
      setNovaHabilidade('');
    }
  };
  const removeHabilidade = (hab: string) => setHabilidades(habilidades.filter((h) => h !== hab));

  // Certificacoes
  const addCertificacao = () => setCertificacoes([...certificacoes, { id: Date.now().toString(), nome: '', instituicao: '', ano: '' }]);
  const updateCertificacao = (id: string, field: keyof Certificacao, value: string) => setCertificacoes(certificacoes.map((c) => c.id === id ? { ...c, [field]: value } : c));
  const removeCertificacao = (id: string) => setCertificacoes(certificacoes.filter((c) => c.id !== id));

  const handleAlbumPhotoAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !user?.userId) return;
    const remaining = 8 - albumPhotos.length;
    const filesToProcess = Array.from(files).slice(0, remaining);
    if (filesToProcess.length === 0) return;

    setUploadingAlbum(true);
    try {
      const results = await lumi.tools.file.upload(filesToProcess);
      const newUrls = results.
      filter((r: any) => r?.fileUrl).
      map((r: any) => r.fileUrl);
      if (newUrls.length > 0) {
        setAlbumPhotos((prev) => {
          const updated = [...prev, ...newUrls].slice(0, 8);
          return updated;
        });
      }
    } catch (error) {
      console.error('Erro ao fazer upload das fotos:', error);
    } finally {
      setUploadingAlbum(false);
    }
    e.target.value = '';
  };

  const removeAlbumPhoto = (index: number) => {
    setAlbumPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  // Marketplace - upload de fotos do anuncio
  const handleAnuncioFotoAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !user?.userId) return;
    const remaining = 8 - anuncioFotos.length;
    const filesToProcess = Array.from(files).slice(0, remaining);
    if (filesToProcess.length === 0) return;

    setUploadingAnuncioFoto(true);
    try {
      const results = await lumi.tools.file.upload(filesToProcess);
      const newUrls = results.
      filter((r: any) => r?.fileUrl).
      map((r: any) => r.fileUrl);
      if (newUrls.length > 0) {
        setAnuncioFotos((prev) => [...prev, ...newUrls].slice(0, 8));
      }
    } catch (error) {
      console.error('Erro ao fazer upload das fotos do anuncio:', error);
    } finally {
      setUploadingAnuncioFoto(false);
    }
    e.target.value = '';
  };

  const removeAnuncioFoto = (index: number) => {
    setAnuncioFotos((prev) => prev.filter((_, i) => i !== index));
  };

  const loadMeusAnuncios = async () => {
    if (!user?.userId) return;
    setLoadingAnuncios(true);
    try {
      const { list } = await lumi.entities.marketplace_anuncios.list({
        filter: { userId: user.userId },
        sort: { createdAt: -1 }
      });
      setMeusAnuncios(list || []);
    } catch (error) {
      console.error('Erro ao carregar anuncios:', error);
    } finally {
      setLoadingAnuncios(false);
    }
  };

  useEffect(() => {
    if (user?.userId && activeTab === 'marketplace') {
      loadMeusAnuncios();
    }
  }, [user?.userId, activeTab]);

  const handlePublicarAnuncio = async () => {
    if (!anuncioTitulo.trim() || !anuncioDescricao.trim() || !anuncioPreco.trim() || !user?.userId) return;
    if (!anuncioCategoria) return;
    setAnuncioSaving(true);
    try {
      const now = new Date().toISOString();
      const precoNum = parseFloat(anuncioPreco);
      await lumi.entities.marketplace_anuncios.create({
        userId: user.userId,
        userName: user.userName || 'Usuario',
        userEmail: user.email || '',
        userPhoto: profileImage || '',
        titulo: anuncioTitulo.trim(),
        descricao: anuncioDescricao.trim(),
        categoria: anuncioCategoria,
        preco: isNaN(precoNum) ? 0 : precoNum,
        localizacao: anuncioLocalizacao.trim() || 'Milao, Italia',
        fotos: anuncioFotos,
        creator: user.userId,
        createdAt: now,
        updatedAt: now
      });
      setAnuncioPublicado(true);
      setTimeout(() => setAnuncioPublicado(false), 3000);
      setShowCriarAnuncio(false);
      setAnuncioFotos([]);
      setAnuncioTitulo('');
      setAnuncioDescricao('');
      setAnuncioPreco('');
      setAnuncioCategoria('');
      setAnuncioLocalizacao('');
      await loadMeusAnuncios();
    } catch (error) {
      console.error('Erro ao publicar anuncio:', error);
    } finally {
      setAnuncioSaving(false);
    }
  };

  const handleDeletarAnuncio = async (anuncioId: string) => {
    if (!anuncioId) return;
    try {
      await lumi.entities.marketplace_anuncios.delete(anuncioId);
      await loadMeusAnuncios();
    } catch (error) {
      console.error('Erro ao deletar anuncio:', error);
    }
  };

  const handleSaveAlbum = async () => {
    if (!user?.userId) return;
    setAlbumSaving(true);
    try {
      const now = new Date().toISOString();
      if (profileRecordId) {
        await lumi.entities.user_profiles.update(profileRecordId, {
          albumPhotos: albumPhotos,
          updatedAt: now
        });
      } else {
        const created = await lumi.entities.user_profiles.create({
          userId: user.userId,
          albumPhotos: albumPhotos,
          creator: user.userId,
          createdAt: now,
          updatedAt: now
        });
        if (created?._id) setProfileRecordId(created._id);
      }
      setAlbumSaved(true);
      setTimeout(() => setAlbumSaved(false), 3000);
    } catch (error) {
      console.error('Erro ao salvar album:', error);
    } finally {
      setAlbumSaving(false);
    }
  };

  // ─── LÓGICA DO LIGHTBOX ───────────────────────────────────────────────────
  const prevFoto = () => {
    if (fotoExpandida !== null && albumPhotos.length > 0) {
      setFotoExpandida(fotoExpandida === 0 ? albumPhotos.length - 1 : fotoExpandida - 1);
    }
  };

  const nextFoto = () => {
    if (fotoExpandida !== null && albumPhotos.length > 0) {
      setFotoExpandida(fotoExpandida === albumPhotos.length - 1 ? 0 : fotoExpandida + 1);
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEndHandler = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    if (isLeftSwipe) {
      nextFoto();
    } else if (isRightSwipe) {
      prevFoto();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (fotoExpandida === null) return;
      if (e.key === 'Escape') setFotoExpandida(null);
      if (e.key === 'ArrowLeft') prevFoto();
      if (e.key === 'ArrowRight') nextFoto();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fotoExpandida, albumPhotos.length]);

  // ─── FUNCOES / AREAS DE ATUACAO (badges no perfil) ─────────────────────────
  const persistirAreasAtuacao = async (novasAreas: string[]) => {
    if (!user?.userId) return;
    setSalvandoFuncoes(true);
    try {
      const now = new Date().toISOString();
      if (profileRecordId) {
        await lumi.entities.user_profiles.update(profileRecordId, {
          areasAtuacao: novasAreas,
          updatedAt: now
        });
      } else {
        const created = await lumi.entities.user_profiles.create({
          userId: user.userId,
          areasAtuacao: novasAreas,
          creator: user.userId,
          createdAt: now,
          updatedAt: now
        });
        if (created?._id) setProfileRecordId(created._id);
      }
    } catch (error) {
      console.error('Erro ao salvar funcoes:', error);
    } finally {
      setSalvandoFuncoes(false);
    }
  };

  const toggleFuncao = (area: string) => {
    const jaTem = areasAtuacao.includes(area);
    const novasAreas = jaTem ?
    areasAtuacao.filter((a) => a !== area) :
    [...areasAtuacao, area];
    setAreasAtuacao(novasAreas);
    persistirAreasAtuacao(novasAreas);
  };

  const removerFuncao = (area: string) => {
    const novasAreas = areasAtuacao.filter((a) => a !== area);
    setAreasAtuacao(novasAreas);
    persistirAreasAtuacao(novasAreas);
  };

  // ─── REFERRAL: link e compartilhamento ─────────────────────────────────────
  const referralLink = referralCode ?
  `${typeof window !== 'undefined' ? window.location.origin : ''}/cadastro?ref=${referralCode}` :
  '';
  const referralShareText = `Junte-se a mim no BBQ Jobs, a plataforma que conecta profissionais da gastronomia a restaurantes brasileira pelo mundo! Use meu codigo de indicacao ${referralCode} ao se cadastrar:`;

  const copiarReferral = async () => {
    if (!referralCode) return;
    try {
      await navigator.clipboard.writeText(referralCode);
      setRefCopied(true);
      setTimeout(() => setRefCopied(false), 2500);
    } catch (e) {
      console.error('Erro ao copiar codigo:', e);
    }
  };

  const copiarLinkReferral = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch (e) {
      console.error('Erro ao copiar link:', e);
    }
  };

  const compartilharEm = (rede: 'facebook' | 'linkedin' | 'twitter') => {
    if (!referralLink) return;
    const url = encodeURIComponent(referralLink);
    const texto = encodeURIComponent(referralShareText);
    let shareUrl = '';
    if (rede === 'facebook') {
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${texto}`;
    } else if (rede === 'linkedin') {
      shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    } else if (rede === 'twitter') {
      shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${texto}`;
    }
    window.open(shareUrl, '_blank', 'width=650,height=550,noopener,noreferrer');
  };

  const compartilharInstagram = () => {
    // Instagram nao permite share direto por URL; abrimos o modal com a imagem do Story
    copiarLinkReferral();
    setShowStoryModal(true);
  };

  const baixarStoryImagem = async () => {
    try {
      const resp = await fetch(REFERRAL_STORY_BG);
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `bbqjobs-story-${referralCode || 'convite'}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    } catch (e) {
      console.error('Erro ao baixar imagem do story:', e);
      window.open(REFERRAL_STORY_BG, '_blank');
    }
  };

  // ─── STRIPE CONNECT ────────────────────────────────────────────────────────
  const STRIPE_FN_URL = `https://api.lumi.new/v1/functions/${'p429616054542557184'}/stripe-connect`;

  const callStripeFn = async (payload: Record<string, any>) => {
    if (isDemoMode) throw new Error('Stripe desativado na demonstração.');
    const token = lumi.auth.accessToken;
    if (!token) throw new Error('Sua sessão não está disponível. Entre novamente para conectar o Stripe.');
    const authorization = `Bearer ${token}`;
    const resp = await fetch(STRIPE_FN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: authorization
      },
      body: JSON.stringify(payload)
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(data?.error || 'Errore nella richiesta Stripe');
    }
    return data;
  };

  const loadStripeData = async (accountId: string) => {
    if (!accountId) return;
    setLoadingStripe(true);
    setStripeError('');
    try {
      const data = await callStripeFn({ action: 'get_account_data', accountId });
      setStripeData(data);
    } catch (e: any) {
      console.error('Errore nel caricamento dati Stripe:', e);
      setStripeError(e instanceof Error ? e.message : 'Não foi possível carregar os dados Stripe.');
    } finally {
      setLoadingStripe(false);
    }
  };

  const handleCreateStripeAccount = async () => {
    if (isDemoMode) { setStripeError('Stripe desativado na demonstração.'); return; }
    if (!user?.userId || creatingStripe) return;
    let win: Window | null = null;
    setCreatingStripe(true);
    setStripeError('');
    try {
      const returnUrl = `${window.location.origin}/perfil`;
      win = window.open('', '_blank');
      if (win) win.opener = null;
      const data = await callStripeFn({
        action: stripeAccountId ? 'get_onboarding_link' : 'create_account',
        accountId: stripeAccountId || undefined,
        returnUrl,
        refreshUrl: returnUrl
      });
      if (data?.accountId) setStripeAccountId(data.accountId);
      if (data?.url && win) {
        win.location.href = data.url;
      } else if (data?.url) {
        window.location.href = data.url;
      } else if (win) {
        win.close();
      }
    } catch (e: any) {
      console.error('Errore nella creazione della conta Stripe:', e);
      if (win) win.close();
      setStripeError(e instanceof Error ? e.message : 'Não foi possível conectar sua conta Stripe.');
    } finally {
      setCreatingStripe(false);
    }
  };

  // Carrega dados da conta Stripe quando ha um accountId e o tab perfil esta ativo
  useEffect(() => {
    if (stripeAccountId && activeTab === 'perfil') {
      loadStripeData(stripeAccountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeAccountId, activeTab]);

  const displayName = user?.userName || 'Usuario';
  const displayEmail = user?.email || '';
  const initials = displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

  const socialItems = [
  { key: 'twitter' as const, label: 'X (Twitter)', icon: <TwitterIcon size={22} />, placeholder: 'https://x.com/seuusuario' },
  { key: 'facebook' as const, label: 'Facebook', icon: <FacebookIcon size={22} />, placeholder: 'https://facebook.com/seuusuario' },
  { key: 'instagram' as const, label: 'Instagram', icon: <InstagramIcon size={22} />, placeholder: 'https://instagram.com/seuusuario' },
  { key: 'youtube' as const, label: 'YouTube', icon: <YoutubeIcon size={22} />, placeholder: 'https://youtube.com/@seucanal' },
  { key: 'linkedin' as const, label: 'LinkedIn', icon: <LinkedinIcon size={22} />, placeholder: 'https://linkedin.com/in/seuusuario' }];


  // Abas do perfil com rótulos responsivos — tambem usadas pela pilula deslizante
  const tabItems = [
  { key: 'perfil', label: 'Perfil', icon: <UserRound size={16} /> },
  { key: 'album', label: 'Álbum', icon: <Image size={16} /> },
  { key: 'marketplace', label: 'Meu Marketplace', icon: <Package size={16} /> },
  { key: 'amigos', label: 'Amigos', icon: <Users size={16} /> },
  { key: 'feed', label: 'Feed', icon: <Rss size={16} /> },
  ...(userProfileType === 'restaurante' ?
  [
  { key: 'vagas', label: 'Minhas Vagas', icon: <Briefcase size={16} /> },
  { key: 'candidatos', label: 'Candidatos', icon: <User size={16} /> },
  { key: 'chat', label: 'Chat', icon: <MessageSquare size={16} /> }] :
  [
  { key: 'curriculo', label: 'Currículo', icon: <FileText size={16} /> },
  { key: 'candidaturas', label: 'Minhas Candidaturas', icon: <Briefcase size={16} /> },
  { key: 'chat', label: 'Chat', icon: <MessageSquare size={16} /> }])] as
  {key: Tab;label: string;icon: React.ReactNode;}[];

  // Move a pilula ate a aba clicada, com duracao proporcional a distancia percorrida
  useEffect(() => {
    const alvo = tabBtnRefs.current[activeTab];
    if (!alvo) return;
    const x = alvo.offsetLeft;
    const largura = alvo.offsetWidth;
    const distancia = Math.abs(x - tabPillXRef.current);
    setTabPillDuration(tabPillPronta ? Math.min(1.1, 0.5 + distancia / 420 * 0.5) : 0);
    tabPillXRef.current = x;
    setTabPill({ x, width: largura });
    if (!tabPillPronta) {
      const timer = window.setTimeout(() => setTabPillPronta(true), 80);
      return () => window.clearTimeout(timer);
    }
  }, [activeTab, userProfileType, tabPillPronta]);

  // Recalcula a posicao da pilula quando a largura da tela muda
  useEffect(() => {
    const aoRedimensionar = () => {
      const alvo = tabBtnRefs.current[activeTab];
      if (!alvo) return;
      tabPillXRef.current = alvo.offsetLeft;
      setTabPillDuration(0);
      setTabPill({ x: alvo.offsetLeft, width: alvo.offsetWidth });
    };
    window.addEventListener('resize', aoRedimensionar);
    return () => window.removeEventListener('resize', aoRedimensionar);
  }, [activeTab]);

  const formatarPeriodo = (inicio: string, fim: string, atual: boolean) => {
    const fmt = (d: string) => {
      if (!d) return '';
      const [y, m] = d.split('-');
      const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      return `${meses[parseInt(m) - 1]}/${y}`;
    };
    if (!inicio && !fim && !atual) return '';
    return `${fmt(inicio)} — ${atual ? 'Presente' : fmt(fim)}`;
  };

  if (!isAuthenticated) return null;

  // ── CURRICULO VIEW ────────────────────────────────────────────────────────────
  const curriculoViewContent =
  <div className="animate-fade-in mt-2">
      {/* Botoes de acao */}
      <div className="flex justify-end gap-3 mb-4">
        <button
        onClick={() => setCurriculoSalvo(false)}
        className="flex items-center gap-2 bg-[#5D4037] text-white px-5 py-2.5 rounded-xl font-medium hover:bg-[#6D4C41] transition-all text-sm shadow-lg border border-[#8D6E63]/30">

          <Edit3 size={14} />
          Editar Curriculo
        </button>
        <button
        onClick={handleSalvarPDF}
        className="flex items-center gap-2 bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-5 py-2.5 rounded-xl font-medium hover:shadow-lg hover:shadow-[#E53935]/30 transition-all text-sm border border-[#8D0000]"
        title="Salvar currículo em PDF (layout clássico, fundo branco)">
          <FileText size={14} />
          Salvar em PDF
        </button>
      </div>

      {/* ═══ Documento CV ═══ */}
      <div className="bg-[#111] rounded-2xl shadow-2xl overflow-hidden mb-8 text-gray-100">

        {/* ── 1. CABECALHO ── */}
        <div className="bg-[#1a1a1a] px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Foto redonda */}
            <div className="shrink-0">
              {profileImage ?
            <img src={profileImage} alt="Foto" className="w-28 h-28 rounded-full object-cover border-4 border-[#E53935]/60 shadow-xl" /> :

            <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center text-white text-3xl font-black border-4 border-[#E53935]/60 shadow-xl">
                  {initials}
                </div>
            }
            </div>

            {/* Dados principais */}
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight tracking-tight">{displayName}</h1>

              {/* Areas de atuacao como subtitulo */}
              {areasAtuacao.length > 0 &&
            <p className="text-[#E57335] font-semibold text-sm mt-1">
                  {areasAtuacao.join(' · ')}
                </p>
            }

              {/* Contatos inline */}
              <div className="flex flex-wrap justify-center sm:justify-start gap-x-4 gap-y-1.5 mt-3">
                {cidade &&
              <span className="flex items-center gap-1.5 text-gray-300 text-xs">
                    <MapPin size={11} className="text-[#E53935]" /> {cidade}
                  </span>
              }
                {telefone &&
              <span className="flex items-center gap-1.5 text-gray-300 text-xs">
                    <Phone size={11} className="text-[#E53935]" /> {telefone}
                  </span>
              }
                {emailContato &&
              <span className="flex items-center gap-1.5 text-gray-300 text-xs">
                    <Mail size={11} className="text-[#E53935]" /> {emailContato}
                  </span>
              }
                {linkedin &&
              <span className="flex items-center gap-1.5 text-gray-300 text-xs">
                    <LinkIcon size={11} className="text-[#E53935]" /> {linkedin}
                  </span>
              }
                {portfolio &&
              <span className="flex items-center gap-1.5 text-gray-300 text-xs">
                    <LinkIcon size={11} className="text-[#E53935]" /> {portfolio}
                  </span>
              }
                {nacionalidade &&
              <span className="flex items-center gap-1.5 text-gray-300 text-xs">
                    <Globe size={11} className="text-[#E53935]" /> {nacionalidade}
                  </span>
              }
              </div>

              {/* Documentacao */}
              {documentacao &&
            <div className="mt-2.5">
                  <span className="inline-flex items-center gap-1.5 bg-green-900/40 text-green-400 text-xs font-semibold px-3 py-1 rounded-full border border-green-700/30">
                    <BadgeCheck size={11} />
                    {DOC_LABELS[documentacao] || documentacao}
                  </span>
                </div>
            }
            </div>
          </div>
        </div>

        {/* ── CORPO (fundo preto) ── */}
        <div className="px-8 pb-8">

          {/* ── 2. RESUMO PROFISSIONAL ── */}
          {objetivo &&
        <CvSection icon={<Target size={14} />} title="Resumo Profissional">
              <p className="text-gray-300 text-sm leading-relaxed">{objetivo}</p>
            </CvSection>
        }

          {/* ── 3. EXPERIENCIA PROFISSIONAL ── */}
          {experiencias.length > 0 &&
        <CvSection icon={<Briefcase size={14} />} title="Experiencia Profissional">
              <div className="space-y-5">
                {experiencias.map((exp, idx) =>
            <div key={exp.id} className="flex gap-4">
                    {/* Timeline dot */}
                    <div className="flex flex-col items-center pt-1 shrink-0">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#E53935]" />
                      {idx < experiencias.length - 1 &&
                <div className="w-px flex-1 bg-gray-700 mt-1" />
                }
                    </div>
                    <div className="flex-1 pb-1">
                      {/* Cargo + periodo */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 mb-0.5">
                        <p className="text-gray-100 font-bold text-sm">{exp.cargo || `Cargo ${idx + 1}`}</p>
                        <span className="text-gray-500 text-xs font-medium">
                          {formatarPeriodo(exp.dataInicio, exp.dataFim, exp.atual)}
                          {exp.atual && <span className="ml-2 text-green-400 font-semibold">• Atual</span>}
                        </span>
                      </div>
                      {/* Empresa + cidade */}
                      <div className="flex flex-wrap gap-x-2 items-center mb-1.5">
                        {exp.restaurante &&
                  <span className="text-[#C62828] text-xs font-bold">{exp.restaurante}</span>
                  }
                        {exp.cidade &&
                  <span className="text-gray-500 text-xs flex items-center gap-0.5">
                            <MapPin size={9} /> {exp.cidade}
                          </span>
                  }
                      </div>
                      {/* Descricao como bullets */}
                      {exp.descricao &&
                <ul className="space-y-0.5">
                          {exp.descricao.split('\n').filter((l) => l.trim()).map((linha, i) =>
                  <li key={i} className="text-gray-400 text-xs leading-relaxed flex gap-2">
                              <span className="text-[#E53935] mt-0.5 shrink-0">•</span>
                              <span>{linha}</span>
                            </li>
                  )}
                          {!exp.descricao.includes('\n') &&
                  <li className="text-gray-400 text-xs leading-relaxed flex gap-2">
                              <span className="text-[#E53935] mt-0.5 shrink-0">•</span>
                              <span>{exp.descricao}</span>
                            </li>
                  }
                        </ul>
                }
                    </div>
                  </div>
            )}
              </div>
            </CvSection>
        }

          {/* ── 4. HABILIDADES ── */}
          {habilidades.length > 0 &&
        <CvSection icon={<Award size={14} />} title="Habilidades e Competencias" color="#8D6E63">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
                {habilidades.map((hab) =>
            <div key={hab} className="flex items-center gap-2 text-xs text-gray-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#8D6E63] shrink-0" />
                    {hab}
                  </div>
            )}
              </div>
            </CvSection>
        }

          {/* ── 5. FORMACAO ACADEMICA ── */}
          {formacoes.length > 0 &&
        <CvSection icon={<GraduationCap size={14} />} title="Formacao Academica" color="#5D8AA8">
              <div className="space-y-2.5">
                {formacoes.map((form) =>
            <div key={form.id} className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-gray-100 font-semibold text-sm">{form.curso || 'Curso'}</p>
                      {form.instituicao &&
                <p className="text-gray-400 text-xs mt-0.5">{form.instituicao}</p>
                }
                    </div>
                    {form.ano &&
              <span className="text-gray-400 text-xs font-medium shrink-0 mt-0.5">{form.ano}</span>
              }
                  </div>
            )}
              </div>
            </CvSection>
        }

          {/* ── 6. IDIOMAS ── */}
          {idiomas.length > 0 &&
        <CvSection icon={<Languages size={14} />} title="Idiomas">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {idiomas.map((idm) =>
            <div key={idm.id} className="flex items-center justify-between py-1.5 px-3 bg-[#1a1a1a] rounded-lg border border-[#333]">
                    <span className="text-gray-200 text-sm font-medium">{idm.idioma || 'Idioma'}</span>
                    <span
                className="text-xs font-bold px-2.5 py-0.5 rounded-full"
                style={{
                  color: NIVEL_COLORS[idm.nivel] || '#999',
                  background: `${NIVEL_COLORS[idm.nivel] || '#999'}18`
                }}>

                      {idm.nivel}
                    </span>
                  </div>
            )}
              </div>
            </CvSection>
        }

          {/* ── 7. CURSOS E CERTIFICACOES ── */}
          {certificacoes.length > 0 &&
        <CvSection icon={<Star size={14} />} title="Cursos e Certificacoes" color="#E57335">
              <div className="space-y-2.5">
                {certificacoes.map((cert) =>
            <div key={cert.id} className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#E57335] shrink-0 mt-1.5" />
                      <div>
                        <p className="text-gray-100 font-semibold text-sm">{cert.nome || 'Certificacao'}</p>
                        {cert.instituicao &&
                  <p className="text-gray-400 text-xs mt-0.5">{cert.instituicao}</p>
                  }
                      </div>
                    </div>
                    {cert.ano &&
              <span className="text-gray-400 text-xs font-medium shrink-0 mt-0.5">{cert.ano}</span>
              }
                  </div>
            )}
              </div>
            </CvSection>
        }

          {/* ── 8. INFORMACOES ADICIONAIS ── */}
          {(disponibilidade || disponibilidadeViagem || cnh || referencias || altura) &&
        <CvSection icon={<Info size={14} />} title="Informacoes Adicionais" color="#555">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {disponibilidade &&
            <div className="flex items-start gap-2 text-xs text-gray-300">
                    <Clock size={13} className="text-gray-500 shrink-0 mt-0.5" />
                    <span><span className="font-semibold">Disponibilidade:</span> {disponibilidade}</span>
                  </div>
            }
                {disponibilidadeViagem &&
            <div className="flex items-start gap-2 text-xs text-gray-300">
                    <CheckCircle2 size={13} className="text-green-400 shrink-0 mt-0.5" />
                    <span>Disponivel para viagens e mudanca de cidade</span>
                  </div>
            }
                {cnh &&
            <div className="flex items-start gap-2 text-xs text-gray-300">
                    <Car size={13} className="text-gray-500 shrink-0 mt-0.5" />
                    <span>Possui Carteira de Habilitacao (CNH)</span>
                  </div>
            }
                {referencias &&
            <div className="flex items-start gap-2 text-xs text-gray-300">
                    <UserRound size={13} className="text-gray-500 shrink-0 mt-0.5" />
                    <span><span className="font-semibold">Referencias:</span> {referencias}</span>
                  </div>
            }
                {altura &&
            <div className="flex items-start gap-2 text-xs text-gray-300">
                    <User size={13} className="text-gray-500 shrink-0 mt-0.5" />
                    <span><span className="font-semibold">Altura:</span> {altura}</span>
                  </div>
            }
              </div>
            </CvSection>
        }

        </div>

        {/* Rodape */}
        <div className="px-8 py-3 bg-[#0a0a0a] border-t border-[#333] flex items-center justify-between">
          <span className="text-gray-500 text-xs">BBQ Jobs — Plataforma de Talentos Gastronômicos</span>
          <span className="text-gray-500 text-xs">{new Date().getFullYear()}</span>
        </div>
      </div>
    </div>;


  // ── CURRICULO EDIT ────────────────────────────────────────────────────────────
  const curriculoEditContent =
  <fieldset disabled={cvSaving} className="space-y-5 mt-1 min-w-0">

      {/* 1. CONTATO */}
      <section className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] p-6 sm:p-8">
        <SectionHeader icon={<Phone size={15} />} label="Contato" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Telefone / WhatsApp" icon={<Phone size={11} />}>
            <input type="text" value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="+39 333 123 4567" className={inputCls} />
          </Field>
          <Field label="E-mail de Contato" icon={<Mail size={11} />}>
            <input type="email" value={emailContato} onChange={(e) => setEmailContato(e.target.value)} placeholder="seu@email.com" className={inputCls} />
          </Field>
          <Field label="Cidade Atual" icon={<MapPin size={11} />}>
            <input type="text" value={cidade} onChange={(e) => setCidade(e.target.value)} placeholder="Milao, Italia" className={inputCls} />
          </Field>
          <Field label="LinkedIn (opcional)" icon={<LinkIcon size={11} />}>
            <input type="url" value={linkedin} onChange={(e) => setLinkedin(e.target.value)} placeholder="https://linkedin.com/in/seuusuario" className={inputCls} />
          </Field>
          <Field label="Situacao Documental" icon={<BadgeCheck size={11} />}>
            <select value={documentacao} onChange={(e) => setDocumentacao(e.target.value)} className={inputCls}>
              <option value="">Selecione...</option>
              <option value="permesso_soggiorno">Permesso di Soggiorno</option>
              <option value="cidadania_europeia">Cidadania Italiana</option>
              <option value="visto_trabalho">Recibo Permesso</option>
              <option value="em_processo">Em Processo</option>
              <option value="outro">Outro</option>
            </select>
          </Field>
          <Field label="Nacionalidade" icon={<Globe size={11} />}>
            <input type="text" value={nacionalidade} onChange={(e) => setNacionalidade(e.target.value)} placeholder="Ex: Brasileira" className={inputCls} />
          </Field>
          <Field label="Disponibilidade de Horario" icon={<Clock size={11} />}>
            <input type="text" value={disponibilidade} onChange={(e) => setDisponibilidade(e.target.value)} placeholder="Ex: Manha, Tarde, Noite, Integral" className={inputCls} />
          </Field>
        </div>
      </section>

      {/* 2. OBJETIVO */}
      <section className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] p-6 sm:p-8">
        <SectionHeader icon={<Target size={15} />} label="Objetivo" />
        <textarea
        value={objetivo}
        onChange={(e) => setObjetivo(e.target.value)}
        rows={4}
        className={inputCls + ' resize-none leading-relaxed'}
        placeholder="Ex: Profissional com 4 anos de experiencia em salao e bar, buscando oportunidades em restaurantes de alto padrao em Milao..." />

        <p className="text-gray-600 text-xs mt-2">Descreva quem voce e e o que busca em 3 a 5 linhas.</p>
      </section>

      {/* 3. MINHAS EXPERIENCIAS */}
      <section className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] p-6 sm:p-8">
        <SectionHeader icon={<Briefcase size={15} />} label="Minhas Experiencias" onAdd={addExperiencia} />
        <p className="text-gray-600 text-xs mb-4">Ordem cronologica inversa (mais recente primeiro).</p>
        <div className="space-y-5">
          {experiencias.length === 0 &&
        <div className="text-center py-8">
              <Briefcase size={30} className="text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Nenhuma experiencia adicionada</p>
              <button onClick={addExperiencia} className="mt-2 text-[#E53935] text-sm hover:underline">+ Adicionar primeira experiencia</button>
            </div>
        }
          {experiencias.map((exp, idx) =>
        <div key={exp.id} className="relative bg-[#1e1e1e] rounded-xl border border-[#3a3a3a] p-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600 font-semibold uppercase tracking-wider">Experiencia {idx + 1}</span>
                {experiencias.length > 0 &&
            <button onClick={() => removeExperiencia(exp.id)} className="text-gray-600 hover:text-red-400 transition-colors">
                    <Trash2 size={15} />
                  </button>
            }
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Cargo / Funcao">
                  <input type="text" value={exp.cargo} onChange={(e) => updateExperiencia(exp.id, 'cargo', e.target.value)} placeholder="Ex: Garcom, Barman, Cuoco..." className={inputSmCls} />
                </Field>
                <Field label="Restaurante / Estabelecimento">
                  <input type="text" value={exp.restaurante} onChange={(e) => updateExperiencia(exp.id, 'restaurante', e.target.value)} placeholder="Ex: Churrascaria Fogo Vivo" className={inputSmCls} />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Cidade" icon={<MapPin size={11} />}>
                  <input type="text" value={exp.cidade} onChange={(e) => updateExperiencia(exp.id, 'cidade', e.target.value)} placeholder="Milao, Italia" className={inputSmCls} />
                </Field>
                <Field label="Inicio" icon={<Calendar size={11} />}>
                  <input type="month" value={exp.dataInicio} onChange={(e) => updateExperiencia(exp.id, 'dataInicio', e.target.value)} className={inputSmCls} />
                </Field>
                <Field label="Fim" icon={<Calendar size={11} />}>
                  <input type="month" value={exp.dataFim} onChange={(e) => updateExperiencia(exp.id, 'dataFim', e.target.value)} disabled={exp.atual} className={inputSmCls + ' disabled:opacity-40 disabled:cursor-not-allowed'} />
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input type="checkbox" checked={exp.atual} onChange={(e) => updateExperiencia(exp.id, 'atual', e.target.checked)} className="w-3.5 h-3.5 rounded border-[#3a3a3a] bg-[#1a1a1a] text-[#E53935]" />
                    <span className="text-gray-500 text-xs">Trabalho atual</span>
                  </label>
                </Field>
              </div>
              <Field label="Descricao (use Enter para separar atividades)">
                <textarea value={exp.descricao} onChange={(e) => updateExperiencia(exp.id, 'descricao', e.target.value)}
            rows={3} placeholder={"Atendi media de 80 clientes por dia\nPreparei drinks classicos e autorais"}
            className={inputSmCls + ' resize-none'} />
              </Field>
            </div>
        )}
        </div>
      </section>

      {/* 4. AREAS DE ATUACAO */}
      <section className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] p-6 sm:p-8">
        <SectionHeader icon={<Briefcase size={15} />} label="Areas de Atuacao" />
        <div className="flex flex-wrap gap-2">
          {AREAS_ATUACAO.map((area) => {
          const selected = areasAtuacao.includes(area);
          return (
            <button
              key={area} type="button"
              onClick={() => setAreasAtuacao(selected ? areasAtuacao.filter((a) => a !== area) : [...areasAtuacao, area])}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
              selected ?
              'bg-[#E53935] border-[#E53935] text-white shadow-md' :
              'bg-[#1e1e1e] border-[#3a3a3a] text-gray-400 hover:border-[#E53935]/50 hover:text-gray-200'}`
              }>
                {selected && <CheckCircle2 size={13} />}
                {area}
              </button>);

        })}
        </div>
        <p className="text-gray-600 text-xs mt-3">Selecione todas as areas em que voce tem experiencia.</p>
      </section>

      {/* 5. IDIOMAS */}
      <section className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] p-6 sm:p-8">
        <SectionHeader icon={<Languages size={15} />} label="Idiomas" onAdd={addIdioma} />
        <div className="space-y-3">
          {idiomas.map((idm) =>
        <div key={idm.id} className="relative bg-[#1e1e1e] rounded-xl border border-[#3a3a3a] p-4">
              {idiomas.length > 0 &&
          <button onClick={() => removeIdioma(idm.id)} className="absolute top-3.5 right-4 text-gray-600 hover:text-red-400 transition-colors">
                  <Trash2 size={15} />
                </button>
          }
              <div className="grid grid-cols-2 gap-4 pr-6">
                <Field label="Idioma">
                  <input type="text" value={idm.idioma} onChange={(e) => updateIdioma(idm.id, 'idioma', e.target.value)} placeholder="Ex: Italiano" className={inputSmCls} />
                </Field>
                <Field label="Nivel">
                  <select value={idm.nivel} onChange={(e) => updateIdioma(idm.id, 'nivel', e.target.value)} className={inputSmCls}>
                    <option value="Basico">Basico</option>
                    <option value="Intermediario">Intermediario</option>
                    <option value="Avancado">Avancado</option>
                    <option value="Fluente">Fluente</option>
                    <option value="Nativo">Nativo</option>
                  </select>
                </Field>
              </div>
            </div>
        )}
        </div>
      </section>

      <section className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] p-6 sm:p-8">
        <SectionHeader icon={<GraduationCap size={15} />} label="Formação" onAdd={addFormacao} />
        {!formacoes.length && <p className="text-gray-400 text-sm">Adicione sua escolaridade ou formação profissional.</p>}
        {formacoes.map(f => <div key={f.id} className="my-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Curso / Escolaridade"><input value={f.curso} onChange={e => updateFormacao(f.id, 'curso', e.target.value)} className={inputSmCls} /></Field>
          <Field label="Instituição"><input value={f.instituicao} onChange={e => updateFormacao(f.id, 'instituicao', e.target.value)} className={inputSmCls} /></Field>
          <Field label="Ano / Situação"><input value={f.ano} onChange={e => updateFormacao(f.id, 'ano', e.target.value)} placeholder="Ex.: 2024 ou em andamento" className={inputSmCls} /></Field>
          <button onClick={() => removeFormacao(f.id)} className="text-red-300 text-sm text-left">Remover formação</button>
        </div>)}
      </section>
      <section className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] p-6 sm:p-8">
        <SectionHeader icon={<Award size={15} />} label="Habilidades" />
        <div className="flex flex-col sm:flex-row gap-3"><input aria-label="Nova habilidade" value={novaHabilidade} onChange={e => setNovaHabilidade(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addHabilidade(); } }} placeholder="Ex.: Atendimento ao cliente" className={inputCls} /><button onClick={addHabilidade} className="text-red-300">Adicionar</button></div>
        <div className="flex flex-wrap gap-2 mt-3">{habilidades.map(h => <button key={h} onClick={() => removeHabilidade(h)} className="bg-[#333] rounded-xl p-2 text-white" aria-label={`Remover habilidade ${h}`}>{h} ×</button>)}</div>
      </section>
      <section className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] p-6 sm:p-8">
        <SectionHeader icon={<BadgeCheck size={15} />} label="Cursos e certificações" onAdd={addCertificacao} />
        {certificacoes.map(c => <div key={c.id} className="my-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Curso / Certificação"><input value={c.nome} onChange={e => updateCertificacao(c.id, 'nome', e.target.value)} className={inputSmCls} /></Field>
          <Field label="Instituição"><input value={c.instituicao} onChange={e => updateCertificacao(c.id, 'instituicao', e.target.value)} className={inputSmCls} /></Field>
          <Field label="Ano"><input value={c.ano} onChange={e => updateCertificacao(c.id, 'ano', e.target.value)} className={inputSmCls} /></Field>
          <button onClick={() => removeCertificacao(c.id)} className="text-red-300 text-sm text-left">Remover curso</button>
        </div>)}
      </section>

      {/* Salvar */}
      <div className="flex flex-col items-center gap-3 pt-2 pb-8">
        <button
        onClick={handleSalvarCurriculo}
        disabled={cvSaving || cvLoading || cvLoadFailed}
        className="flex items-center gap-3 bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-14 py-4 rounded-xl font-bold text-base hover:shadow-xl hover:shadow-[#E53935]/30 transition-all border border-[#8D0000] hover:-translate-y-0.5">
          <Save size={20} />
          {cvSaving ? 'Salvando...' : cvCloud ? 'Salvar currículo' : 'Salvar neste navegador'}
        </button>

      </div>
    </fieldset>;


  if (!profileReady) return <div className="min-h-screen bg-[#1a1a1a] text-white"><Navbar /><main className="p-8 text-center">{profileLoadError ? <><p>Não foi possível carregar seu perfil.</p><button className="underline mt-3" onClick={() => window.location.reload()}>Tentar novamente</button></> : <p>Carregando perfil...</p>}</main></div>;

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex flex-col">
      <Navbar />

      {/* Banner */}
      <div className="relative h-[240px] sm:h-[310px] lg:h-[380px] overflow-hidden bg-[#241b18]">
        <img
          src={userProfileType === 'restaurante' ? customBannerUrl || BANNER_IMAGES[setor] : BANNER_IMAGES[setor]}
          alt={userProfileType === 'restaurante' ? 'Foto de capa do estabelecimento' : 'Capa do perfil profissional'}
          className="absolute inset-0 w-full h-full object-cover" />

        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-black/15 to-black/10"></div>
        <div className="absolute top-3 right-3 sm:top-4 sm:right-6 z-20">
          {userProfileType === 'restaurante' ?
          <>
              <input
              type="file"
              ref={bannerInputRef}
              onChange={handleBannerChange}
              accept="image/*"
              className="hidden" />

              <button
              onClick={() => bannerInputRef.current?.click()}
              disabled={uploadingBanner}
              className="flex items-center gap-2 bg-black/60 backdrop-blur-md text-white px-4 py-2.5 rounded-xl text-sm hover:bg-black/80 transition-all border border-white/15 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
              title="Escolher foto da fachada ou do interior do restaurante"
              aria-label="Alterar foto de capa">
                {uploadingBanner ?
              <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span className="font-medium">Enviando...</span>
                  </> :

              <>
                    <Camera size={15} />
                    <span className="font-semibold">{customBannerUrl ? 'Alterar foto de capa' : 'Adicionar foto de capa'}</span>
                  </>
              }
              </button>
              <p className="mt-2 max-w-[250px] rounded-lg bg-black/55 px-3 py-2 text-xs leading-relaxed text-white/90 backdrop-blur-sm">
                Mostre a fachada ou o interior do seu restaurante. Prefira uma foto horizontal.
              </p>
            </> :

          <div className="relative">
              <button
              onClick={() => setShowSetorDropdown(!showSetorDropdown)}
              className="flex items-center gap-2 bg-black/60 backdrop-blur-md text-white px-4 py-2.5 rounded-xl text-sm hover:bg-black/80 transition-all border border-white/15 shadow-lg">

                {SETORES.find((s) => s.key === setor)?.icon}
                <span className="font-medium">{SETORES.find((s) => s.key === setor)?.label}</span>
                <ChevronDown size={14} className={`transition-transform ${showSetorDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showSetorDropdown &&
            <div className="absolute right-0 top-full mt-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded-xl shadow-2xl overflow-hidden min-w-[200px] z-30 animate-fade-in">
                  <div className="px-4 py-2.5 border-b border-[#3a3a3a]">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Escolha seu setor</p>
                  </div>
                  {SETORES.map((s) =>
              <button key={s.key} onClick={() => {setSetor(s.key);setShowSetorDropdown(false);}}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all ${setor === s.key ? 'bg-[#E53935]/15 text-[#E53935] font-semibold' : 'text-gray-300 hover:bg-[#3a3a3a] hover:text-white'}`}>

                      {s.icon}
                      <span>{s.label}</span>
                      {setor === s.key && <div className="ml-auto w-2 h-2 rounded-full bg-[#E53935]" />}
                    </button>
              )}
                </div>
            }
            </div>
          }
        </div>
      </div>

      {/* Content */}
      <div className="w-[94%] sm:w-[92%] max-w-[1320px] mx-auto -mt-12 sm:-mt-16 relative z-10 pb-16">

        {/* Profile header card */}
        <div className="bg-gradient-to-br from-[#292929] to-[#202020] rounded-3xl border border-white/10 shadow-2xl overflow-visible mb-5 relative">
          {/* Avatar sobressaindo o card */}
          <div className="absolute left-1/2 -translate-x-1/2 sm:left-10 sm:translate-x-0 -top-[58px] sm:-top-[80px] z-20 group">
            <input type="file" ref={fileInputRef} onChange={handleProfileImageChange} accept="image/*" className="hidden" />
            <div className="relative">
              {profileImage ?
              <img src={profileImage} alt="Foto de perfil" className={`w-[100px] h-[100px] sm:w-[140px] sm:h-[140px] md:w-[160px] md:h-[160px] rounded-full object-cover border-4 sm:border-[5px] border-[#2a2a2a] shadow-[0_8px_32px_rgba(0,0,0,0.5)] ring-2 ${userProfileType === 'restaurante' ? 'ring-[#E53935]' : 'ring-white'}`} /> :
              <div className={`w-[100px] h-[100px] sm:w-[140px] sm:h-[140px] md:w-[160px] md:h-[160px] rounded-full bg-gradient-to-br from-[#C62828] via-[#E53935] to-[#FF5722] flex items-center justify-center text-white text-4xl sm:text-5xl md:text-6xl font-black border-4 sm:border-[5px] border-[#2a2a2a] shadow-[0_8px_32px_rgba(0,0,0,0.5)] ring-2 ${userProfileType === 'restaurante' ? 'ring-[#E53935]' : 'ring-white'}`}>
                  {initials}
                </div>
              }
              {/* Camera overlay - sempre visibile per garantire il caricamento della foto */}
              <button onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-0 right-0 sm:bottom-2 sm:right-2 w-8 h-8 sm:w-10 sm:h-10 bg-[#E53935] rounded-full flex items-center justify-center text-white hover:bg-[#C62828] transition-all border-[3px] border-[#2a2a2a] shadow-lg opacity-100 hover:scale-110 cursor-pointer"
              title="Alterar foto de perfil"
              disabled={uploadingPhoto}>
                {uploadingPhoto ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Camera size={16} />}
              </button>
              {/* Online indicator */}
              <div className="absolute top-1 right-1 sm:top-2 sm:right-2 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-green-500 rounded-full border-[3px] border-[#2a2a2a] shadow-md" />
            </div>
          </div>

          <div className="px-5 sm:px-8 pt-[58px] sm:pt-7 pb-7">
            <div className="flex flex-col xl:flex-row items-center xl:items-start gap-5">
              <div className="flex-1 w-full text-center sm:text-left min-w-0 sm:pl-[185px] md:pl-[205px]">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#efb39a]">{userProfileType === 'restaurante' ? 'Perfil do estabelecimento' : 'Perfil profissional'}</p>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-white tracking-tight break-words">{displayName}</h1>
                {displayEmail && <p className="text-gray-400 mt-0.5 truncate text-xs sm:text-sm">{displayEmail}</p>}
                <div className="flex items-center justify-center sm:justify-start gap-2 mt-2 text-gray-300 text-sm">
                  <MapPin size={14} className="text-[#E53935]" />
                  <span>{cidade || 'Cidade não informada'}</span>
                </div>
                {userProfileType !== 'restaurante' &&
                <div className="mt-2.5 flex flex-wrap items-center gap-2 justify-center sm:justify-start">
                    <span className="inline-flex items-center gap-1.5 bg-[#E53935]/10 text-[#E53935] text-xs font-semibold px-3.5 py-1.5 rounded-full border border-[#E53935]/20 backdrop-blur-sm">
                      {SETORES.find((s) => s.key === setor)?.icon}
                      {SETORES.find((s) => s.key === setor)?.label}
                    </span>

                    {/* Funcoes selecionadas (badges) */}
                    {areasAtuacao.map((area) =>
                  <span
                    key={area}
                    className="group/func inline-flex items-center gap-1.5 bg-[#8D6E63]/15 text-[#D7C3BB] text-xs font-semibold px-3 py-1.5 rounded-full border border-[#8D6E63]/30 backdrop-blur-sm">
                        <Briefcase size={12} className="text-[#C9A99C]" />
                        {area}
                        <button
                      onClick={() => removerFuncao(area)}
                      title="Remover funcao"
                      className="ml-0.5 text-[#C9A99C] hover:text-red-400 transition-colors opacity-0 group-hover/func:opacity-100">
                          <XIcon size={12} />
                        </button>
                      </span>
                  )}

                    {/* Botao + para adicionar funcoes */}
                    <div className="relative">
                      <button
                      onClick={() => setShowFuncoesDropdown((v) => !v)}
                      title="Adicionar funcoes"
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-[#E53935] text-white shadow-md hover:bg-[#C62828] hover:scale-110 transition-all border-2 border-[#2a2a2a]">
                        <Plus size={15} className={`transition-transform ${showFuncoesDropdown ? 'rotate-45' : ''}`} />
                      </button>
                      {showFuncoesDropdown &&
                    <>
                          <div className="fixed inset-0 z-30" onClick={() => setShowFuncoesDropdown(false)} />
                          <div className="absolute left-0 top-full mt-2 bg-[#2a2a2a] border border-[#3a3a3a] rounded-xl shadow-2xl overflow-hidden min-w-[220px] z-40 animate-fade-in">
                            <div className="px-4 py-2.5 border-b border-[#3a3a3a] flex items-center justify-between">
                              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Suas funcoes</p>
                              {salvandoFuncoes && <div className="w-3.5 h-3.5 border-2 border-[#E53935] border-t-transparent rounded-full animate-spin" />}
                            </div>
                            <div className="max-h-64 overflow-y-auto py-1">
                              {AREAS_ATUACAO.map((area) => {
                            const selecionada = areasAtuacao.includes(area);
                            return (
                              <button
                                key={area}
                                onClick={() => toggleFuncao(area)}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all text-left ${selecionada ? 'bg-[#E53935]/15 text-[#E53935] font-semibold' : 'text-gray-300 hover:bg-[#3a3a3a] hover:text-white'}`}>
                                    <span className={`w-4 h-4 rounded flex items-center justify-center shrink-0 border ${selecionada ? 'bg-[#E53935] border-[#E53935]' : 'border-[#555]'}`}>
                                      {selecionada && <CheckCircle2 size={12} className="text-white" />}
                                    </span>
                                    <span>{area}</span>
                                  </button>);

                          })}
                            </div>
                          </div>
                        </>
                    }
                    </div>
                  </div>
                }
              </div>
              <div className="flex flex-wrap justify-center sm:justify-start gap-2 sm:gap-3 shrink-0 mt-1 xl:mt-0 items-center w-full xl:w-auto sm:pl-[185px] md:pl-[205px] xl:pl-0">
                {userProfileType === 'restaurante' &&
                <Link
                  to="/criar-vaga"
                  className="flex items-center gap-2 bg-[#C62828] text-white px-5 py-2.5 rounded-xl font-medium hover:bg-[#E53935] transition-all text-sm border border-[#8D0000]">

                    <Briefcase size={16} /> Criar Vaga
                  </Link>
                }
                {activeTab === 'perfil' && (
                !isEditing ?
                <>
                      <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 bg-[#3a3a3a] text-gray-200 px-5 py-2.5 rounded-xl font-medium hover:bg-[#4a4a4a] transition-all text-sm border border-[#4a4a4a]">
                        <Edit3 size={16} /> Editar Perfil
                      </button>
                    </> :

                <>
                      <button onClick={handleCancel} className="flex items-center gap-2 bg-[#3a3a3a] text-gray-300 px-4 py-2.5 rounded-xl font-medium hover:bg-[#4a4a4a] transition-all text-sm border border-[#4a4a4a]">
                        <XIcon size={16} /> Cancelar
                      </button>
                      <button onClick={handleSave} className="flex items-center gap-2 bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-5 py-2.5 rounded-xl font-medium hover:shadow-lg hover:shadow-[#E53935]/30 transition-all text-sm">
                        <Save size={16} /> Salvar
                      </button>
                    </>)

                }
              </div>
            </div>
          </div>

          {/* Navegação com rótulos em telas grandes e pílula deslizante preservada. */}
          <div className="relative flex items-stretch border-t border-white/10 overflow-x-auto rounded-b-3xl bg-black/10">
            {/* Pilula deslizante (fundo) */}
            <div
              aria-hidden="true"
              className="absolute top-1 bottom-[3px] rounded-xl bg-white border border-white/80 shadow-sm pointer-events-none"
              style={{
                width: tabPill.width ? tabPill.width - 4 : 0,
                transform: `translateX(${tabPill.x + 2}px)`,
                opacity: tabPill.width ? 1 : 0,
                transition: `transform ${tabPillDuration}s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s ease, opacity 0.2s ease`
              }} />

            {/* Barra inferior deslizante */}
            <div
              aria-hidden="true"
              className="absolute bottom-0 h-[2px] rounded-full bg-[#E53935] pointer-events-none"
              style={{
                width: tabPill.width ? tabPill.width - 10 : 0,
                transform: `translateX(${tabPill.x + 5}px)`,
                opacity: tabPill.width ? 1 : 0,
                transition: `transform ${tabPillDuration}s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s ease, opacity 0.2s ease`
              }} />

            {tabItems.map((tab) =>
            <button key={tab.key}
            ref={(el) => {tabBtnRefs.current[tab.key] = el;}}
            onClick={() => setActiveTab(tab.key)}
            title={tab.label}
            aria-label={tab.label}
            aria-current={activeTab === tab.key ? 'page' : undefined}
            className={`relative z-10 flex-1 min-w-[42px] lg:min-w-max flex items-center justify-center gap-2 px-1 lg:px-4 py-4 transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-[#E53935] ${
            activeTab === tab.key ?
            'text-[#221b18] font-semibold' :
            'text-gray-300 hover:text-white'}`
            }>

                <span className="[&_svg]:w-4 [&_svg]:h-4 sm:[&_svg]:w-[18px] sm:[&_svg]:h-[18px]">{tab.icon}</span>
                <span className="hidden lg:inline whitespace-nowrap text-xs xl:text-sm">{tab.label}</span>
                {tab.key === 'curriculo' && curriculoSalvo &&
              <span className="absolute top-2 right-1/2 translate-x-[10px] sm:translate-x-[14px] lg:right-2 lg:translate-x-0 w-2 h-2 rounded-full bg-green-500" title="Currículo salvo" />
              }
                {tab.key === 'amigos' && solicitacoesPendentes.length > 0 &&
              <span className="absolute top-1.5 right-1/2 translate-x-[11px] sm:translate-x-[16px] lg:right-1 lg:translate-x-0 w-[15px] h-[15px] rounded-full bg-[#E53935] text-white text-[9px] font-bold flex items-center justify-center" title="Solicitacoes pendentes">{solicitacoesPendentes.length}</span>
              }
              </button>
            )}
          </div>
        </div>

        {showExcluirModal && <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80" onClick={() => setShowExcluirModal(false)}>
          <div role="dialog" aria-modal="true" aria-labelledby="delete-title" className="w-full max-w-md rounded-2xl bg-[#1e1e1e] p-6 text-gray-300" onClick={e => e.stopPropagation()}>
            <h2 id="delete-title" className="text-white font-bold mb-3">Exclusão de conta indisponível</h2>
            <p>A exclusão completa ainda não está disponível neste protótipo. Nenhum dado foi apagado. Entre em contato com o responsável pelo site para solicitar a remoção.</p>
            <button className="mt-5 rounded-xl bg-[#333] px-5 py-3" onClick={() => setShowExcluirModal(false)}>Entendi</button>
          </div>
        </div>}

        {/* TAB: ALBUM */}
        {activeTab === 'album' &&
        <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 mt-5">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2.5">
                  <div className="w-1 h-5 rounded-full bg-[#E53935]" />
                  <Image size={18} className="text-[#E53935]" />
                  Meu Album de Fotos
                </h2>
                <p className="text-gray-500 text-xs mt-1 ml-8">Adicione ate 8 fotos para mostrar seu trabalho e experiencia</p>
              </div>
              {albumPhotos.length < 8 &&
            <button
              onClick={() => albumInputRef.current?.click()}
              className="flex items-center gap-2 bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-5 py-2.5 rounded-xl font-medium hover:shadow-lg hover:shadow-[#E53935]/30 transition-all text-sm border border-[#8D0000]">

                {uploadingAlbum ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Plus size={16} />}
                {uploadingAlbum ? 'Enviando...' : 'Adicionar Foto'}
              </button>
            }
            </div>
            <input type="file" ref={albumInputRef} onChange={handleAlbumPhotoAdd} accept="image/*" multiple className="hidden" />

            {albumPhotos.length === 0 ?
          <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-[#3a3a3a] rounded-2xl">
                <div className="w-20 h-20 rounded-full bg-[#333] flex items-center justify-center mb-4">
                  <Image size={36} className="text-gray-600" />
                </div>
                <p className="text-gray-400 text-base font-semibold mb-1">Nenhuma foto adicionada</p>
                <p className="text-gray-600 text-sm mb-5">Mostre seu trabalho! Adicione fotos de pratos, eventos ou do seu dia a dia.</p>
                <button
              onClick={() => albumInputRef.current?.click()}
              className="flex items-center gap-2 bg-[#E53935] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#C62828] transition-all text-sm">

                  <Camera size={16} />
                  Escolher Fotos
                </button>
              </div> :

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {albumPhotos.map((photo, index) =>
            <div key={index} className="relative group aspect-square rounded-xl overflow-hidden border border-[#3a3a3a] shadow-lg cursor-pointer" onClick={() => setFotoExpandida(index)}>
                    <img src={photo} alt={`Foto ${index + 1}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
                    <button
                onClick={(e) => { e.stopPropagation(); removeAlbumPhoto(index); }}
                className="absolute top-2 right-2 w-8 h-8 bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-red-600 border border-white/20">

                      <XIcon size={14} />
                    </button>
                    <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                      {index + 1} / {albumPhotos.length}
                    </div>
                  </div>
            )}
                {albumPhotos.length < 8 &&
            <button
              onClick={() => albumInputRef.current?.click()}
              className="aspect-square rounded-xl border-2 border-dashed border-[#3a3a3a] flex flex-col items-center justify-center gap-2 text-gray-500 hover:border-[#E53935]/50 hover:text-[#E53935] transition-all cursor-pointer hover:bg-[#E53935]/5">

                    <Plus size={28} />
                    <span className="text-xs font-medium">Adicionar</span>
                  </button>
            }
              </div>
          }

            {albumPhotos.length > 0 &&
          <div className="flex flex-col items-center gap-4 mt-6">
                <p className="text-gray-600 text-xs">{albumPhotos.length} de 8 fotos adicionadas</p>
                <button
              onClick={handleSaveAlbum}
              disabled={albumSaving}
              className="flex items-center gap-2.5 bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-10 py-3.5 rounded-xl font-bold text-sm hover:shadow-xl hover:shadow-[#E53935]/30 transition-all border border-[#8D0000] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed">
                  {albumSaving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={17} />}
                  {albumSaving ? 'Salvando...' : 'Salvar Album'}
                </button>
                {albumSaved &&
            <div className="flex items-center gap-2 text-green-400 text-sm animate-fade-in">
                  <CheckCircle2 size={16} />
                  Album salvo com sucesso!
                </div>
            }
              </div>
          }
          </div>
        }

        {/* TAB: MEU MARKETPLACE */}
        {activeTab === 'marketplace' &&
        <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 mt-5">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2.5">
                  <div className="w-1 h-5 rounded-full bg-[#8D6E63]" />
                  <Package size={18} className="text-[#8D6E63]" />
                  Meu Marketplace
                </h2>
                <p className="text-gray-500 text-xs mt-1 ml-8">Gerencie seus anuncios de desapegos, alugueis e muito mais</p>
              </div>
              <button
              onClick={() => setShowCriarAnuncio(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-[#8D6E63] to-[#6D4C41] text-white px-5 py-2.5 rounded-xl font-medium hover:shadow-lg hover:shadow-[#8D6E63]/30 transition-all text-sm">
                <Plus size={16} />
                Criar Anuncio
              </button>
            </div>

            {anuncioPublicado &&
          <div className="flex items-center gap-2 text-green-400 text-sm animate-fade-in bg-green-500/10 border border-green-500/20 px-4 py-3 rounded-xl mb-5">
              <CheckCircle2 size={16} />
              Anuncio publicado com sucesso! Ja esta visivel no Marketplace.
            </div>
          }

            {loadingAnuncios ?
          <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-3 border-[#8D6E63] border-t-transparent rounded-full animate-spin" />
            </div> :
          meusAnuncios.length === 0 ?
          <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-[#3a3a3a] rounded-2xl">
              <div className="w-20 h-20 rounded-full bg-[#333] flex items-center justify-center mb-4">
                <Package size={36} className="text-gray-600" />
              </div>
              <p className="text-gray-400 text-base font-semibold mb-1">Nenhum anuncio publicado</p>
              <p className="text-gray-600 text-sm mb-5 text-center max-w-md">Publique anuncios de desapegos, aluguel de quartos, apartamentos e muito mais no Marketplace.</p>
              <button
              onClick={() => setShowCriarAnuncio(true)}
              className="flex items-center gap-2 bg-[#8D6E63] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#6D4C41] transition-all text-sm">
                <Plus size={16} />
                Criar Meu Primeiro Anuncio
              </button>
            </div> :
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {meusAnuncios.map((anuncio) =>
            <div key={anuncio._id} className="bg-[#1e1e1e] border border-[#3a3a3a] rounded-xl overflow-hidden hover:border-[#8D6E63]/50 transition-all group">
                <div className="relative h-44 bg-[#111] overflow-hidden">
                  {anuncio.fotos && anuncio.fotos.length > 0 ?
                <img src={anuncio.fotos[0]} alt={anuncio.titulo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> :
                <div className="w-full h-full flex items-center justify-center">
                    <Package size={40} className="text-gray-700" />
                  </div>
                }
                  <div className="absolute top-3 right-3 bg-[#8D6E63] text-white px-3 py-1 rounded-full text-xs font-bold capitalize">
                    {anuncio.categoria}
                  </div>
                  <button
                  onClick={() => handleDeletarAnuncio(anuncio._id)}
                  className="absolute top-3 left-3 w-8 h-8 bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all hover:bg-red-600"
                  title="Deletar anuncio">
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="p-4">
                  <h3 className="text-white font-bold text-base mb-1 line-clamp-1">{anuncio.titulo}</h3>
                  <p className="text-gray-500 text-xs mb-3 line-clamp-2">{anuncio.descricao}</p>
                  <div className="flex items-center justify-between pt-3 border-t border-[#333]">
                    <div className="flex items-center gap-1.5 text-gray-500 text-xs">
                      <MapPin size={11} /> {anuncio.localizacao}
                    </div>
                    <span className="text-[#E57335] font-black text-lg">€{anuncio.preco}</span>
                  </div>
                </div>
              </div>
            )}
            </div>
          }
          </div>
        }

        {/* MODAL: CRIAR ANUNCIO */}
        {showCriarAnuncio &&
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pt-20" onClick={() => setShowCriarAnuncio(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative bg-[#222] border border-[#3a3a3a] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header do modal */}
            <div className="sticky top-0 z-10 bg-[#222] border-b border-[#3a3a3a] px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-lg font-bold text-white flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-[#8D6E63]/20 flex items-center justify-center">
                  <Package size={16} className="text-[#8D6E63]" />
                </div>
                Criar Novo Anuncio
              </h2>
              <button onClick={() => setShowCriarAnuncio(false)} className="w-9 h-9 rounded-full bg-[#333] flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#444] transition-all">
                <XIcon size={18} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Fotos do Produto */}
              <div>
                <label className="flex items-center gap-2 text-gray-300 text-sm font-semibold mb-3">
                  <Camera size={14} className="text-[#8D6E63]" />
                  Fotos do Produto
                  <span className="text-gray-600 text-xs font-normal ml-1">(ate 8 fotos)</span>
                </label>
                <input type="file" ref={anuncioFotoRef} onChange={handleAnuncioFotoAdd} accept="image/*" multiple className="hidden" />

                {anuncioFotos.length === 0 ?
                <button
                  onClick={() => anuncioFotoRef.current?.click()}
                  disabled={uploadingAnuncioFoto}
                  className="w-full py-12 border-2 border-dashed border-[#3a3a3a] rounded-xl flex flex-col items-center justify-center gap-3 text-gray-500 hover:border-[#8D6E63]/50 hover:text-[#8D6E63] hover:bg-[#8D6E63]/5 transition-all cursor-pointer">
                  {uploadingAnuncioFoto ?
                  <div className="w-6 h-6 border-2 border-[#8D6E63] border-t-transparent rounded-full animate-spin" /> :
                  <>
                      <div className="w-14 h-14 rounded-full bg-[#333] flex items-center justify-center">
                        <Image size={24} className="text-gray-600" />
                      </div>
                      <span className="text-sm font-medium">Clique para adicionar fotos</span>
                      <span className="text-xs text-gray-600">JPG, PNG ou WebP</span>
                    </>
                  }
                </button> :
                <div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {anuncioFotos.map((foto, index) =>
                    <div key={index} className="relative group aspect-square rounded-xl overflow-hidden border border-[#3a3a3a]">
                      <img src={foto} alt={`Foto ${index + 1}`} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all" />
                      <button
                        onClick={() => removeAnuncioFoto(index)}
                        className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all hover:bg-red-600 border border-white/20">
                        <XIcon size={11} />
                      </button>
                      <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                        {index + 1}
                      </div>
                    </div>
                    )}
                    {anuncioFotos.length < 8 &&
                    <button
                      onClick={() => anuncioFotoRef.current?.click()}
                      disabled={uploadingAnuncioFoto}
                      className="aspect-square rounded-xl border-2 border-dashed border-[#3a3a3a] flex flex-col items-center justify-center gap-1.5 text-gray-500 hover:border-[#8D6E63]/50 hover:text-[#8D6E63] transition-all cursor-pointer">
                      {uploadingAnuncioFoto ?
                      <div className="w-5 h-5 border-2 border-[#8D6E63] border-t-transparent rounded-full animate-spin" /> :
                      <>
                          <Plus size={20} />
                          <span className="text-[10px] font-medium">Adicionar</span>
                        </>
                      }
                    </button>
                    }
                  </div>
                  <p className="text-gray-600 text-xs mt-2 text-center">{anuncioFotos.length} de 8 fotos</p>
                </div>
                }
              </div>

              {/* Titulo */}
              <div>
                <label className="flex items-center gap-2 text-gray-300 text-sm font-semibold mb-2">
                  <Edit3 size={14} className="text-[#8D6E63]" />
                  Titulo do Anuncio
                </label>
                <input
                  type="text"
                  value={anuncioTitulo}
                  onChange={(e) => setAnuncioTitulo(e.target.value)}
                  placeholder="Ex: Quarto individual proximo ao metro"
                  className={inputCls} />

              </div>

              {/* Categoria e Localizacao */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-gray-300 text-sm font-semibold mb-2">
                    <Package size={14} className="text-[#8D6E63]" />
                    Categoria
                  </label>
                  <select
                    value={anuncioCategoria}
                    onChange={(e) => setAnuncioCategoria(e.target.value)}
                    className={inputCls}>
                    <option value="">Selecione...</option>
                    <option value="quarto">Quarto</option>
                    <option value="cama">Cama</option>
                    <option value="apartamento">Apartamento</option>
                    <option value="desapego">Desapego</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-gray-300 text-sm font-semibold mb-2">
                    <MapPin size={14} className="text-[#8D6E63]" />
                    Localizacao
                  </label>
                  <input
                    type="text"
                    value={anuncioLocalizacao}
                    onChange={(e) => setAnuncioLocalizacao(e.target.value)}
                    placeholder="Ex: Centro, Milao"
                    className={inputCls} />

                </div>
              </div>

              {/* Descricao */}
              <div>
                <label className="flex items-center gap-2 text-gray-300 text-sm font-semibold mb-2">
                  <FileText size={14} className="text-[#8D6E63]" />
                  Descricao
                </label>
                <textarea
                  value={anuncioDescricao}
                  onChange={(e) => setAnuncioDescricao(e.target.value)}
                  rows={4}
                  placeholder="Descreva seu produto ou servico em detalhes..."
                  className={inputCls + ' resize-none leading-relaxed'} />

              </div>

              {/* Preco */}
              <div>
                <label className="flex items-center gap-2 text-gray-300 text-sm font-semibold mb-2">
                  <span className="text-[#8D6E63] font-bold text-base">€</span>
                  Preco
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8D6E63] font-bold text-lg">€</span>
                  <input
                    type="number"
                    value={anuncioPreco}
                    onChange={(e) => setAnuncioPreco(e.target.value)}
                    placeholder="0,00"
                    min="0"
                    step="0.01"
                    className={inputCls + ' pl-10 text-lg font-bold'} />

                </div>
              </div>
            </div>

            {/* Footer do modal */}
            <div className="sticky bottom-0 bg-[#222] border-t border-[#3a3a3a] px-6 py-4 flex items-center justify-between gap-4 rounded-b-2xl">
              <button
                onClick={() => setShowCriarAnuncio(false)}
                className="flex items-center gap-2 bg-[#333] text-gray-300 px-5 py-2.5 rounded-xl font-medium hover:bg-[#444] transition-all text-sm border border-[#444]">
                <XIcon size={15} />
                Cancelar
              </button>
              <button
                onClick={handlePublicarAnuncio}
                disabled={anuncioSaving || !anuncioTitulo.trim() || !anuncioDescricao.trim() || !anuncioPreco.trim() || !anuncioCategoria}
                className="flex items-center gap-2 bg-gradient-to-r from-[#8D6E63] to-[#6D4C41] text-white px-8 py-2.5 rounded-xl font-bold hover:shadow-lg hover:shadow-[#8D6E63]/30 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                {anuncioSaving ?
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> :
                <CheckCircle2 size={16} />
                }
                {anuncioSaving ? 'Publicando...' : 'Publicar Anuncio'}
              </button>
            </div>
          </div>
        </div>
        }

        {/* TAB: PERFIL */}
        {activeTab === 'perfil' &&
        <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 space-y-8 mt-1">
            <div>
              <SectionHeader icon={null} label="Sobre mim" />
              {isEditing ?
            <textarea value={editBio} onChange={(e) => setEditBio(e.target.value)} rows={4} className={inputCls + ' resize-none leading-relaxed'} placeholder="Conte um pouco sobre voce..." /> :

            <p className="text-gray-400 leading-relaxed text-sm">{biografia}</p>
            }
            </div>
            <div>
              <SectionHeader icon={null} label="Redes Sociais" accentColor="#8D6E63" />
              {isEditing ?
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {socialItems.map((item) =>
              <div key={item.key}>
                      <label className="flex items-center gap-2 text-gray-400 text-xs font-medium mb-1.5">
                        <span className="[&_svg]:w-[14px] [&_svg]:h-[14px]">{item.icon}</span>
                        {item.label}
                      </label>
                      <input type="url" value={editSocial[item.key]} onChange={(e) => setEditSocial({ ...editSocial, [item.key]: e.target.value })} placeholder={item.placeholder} className={inputCls} />
                    </div>
              )}
                </div> :

            <div className="flex items-center gap-4 flex-wrap">
                  {socialItems.map((item) => {
                const link = socialLinks[item.key];
                return link ?
                <a key={item.key} href={link} target="_blank" rel="noopener noreferrer"
                className="w-[43px] h-[43px] rounded-full bg-[#5D4037] flex items-center justify-center text-white hover:scale-125 transition-all duration-300 shadow-md hover:shadow-xl hover:shadow-[#5D4037]/60 hover:-translate-y-1"
                title={item.label}>
                        <span className="[&_svg]:w-[22px] [&_svg]:h-[22px] [&_svg]:fill-white">{item.icon}</span>
                      </a> :

                <div key={item.key} className="w-[43px] h-[43px] rounded-full bg-[#5D4037]/40 flex items-center justify-center cursor-default hover:scale-125 transition-all duration-300" title={`${item.label} - Nao informado`}>
                        <span className="[&_svg]:w-[22px] [&_svg]:h-[22px] [&_svg]:fill-white/30">{item.icon}</span>
                      </div>;

              })}
                </div>
            }
            </div>

            {/* ── CONVIDE E GANHE + MINHA CONTA STRIPE (lado a lado) ── */}
            <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* ── CONVIDE E GANHE (REFERRAL) — visivel apenas para o dono do perfil logado ── */}
            {isAuthenticated && user?.userId && referralCode &&
            <div className="flex-1 w-full">
                <SectionHeader icon={<Gift size={15} />} label="Convide e ganhe" />
                <div className="bg-gradient-to-br from-[#1e1e1e] to-[#242424] border border-[#3a3a3a] rounded-2xl p-5">
                  <p className="text-gray-500 text-xs mb-4">Compartilhe seu codigo de indicacao e ganhe recompensas quando novos usuarios se cadastrarem.</p>

                  {/* Codigo + copiar */}
                  <div className="flex items-center justify-between bg-[#111] border border-dashed border-[#E53935]/40 rounded-xl px-4 py-3">
                    <span className="text-[#E57335] font-black tracking-[0.2em] text-lg select-all">{referralCode}</span>
                    <button
                    onClick={copiarReferral}
                    title="Copiar codigo"
                    className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors text-xs font-medium">
                      {refCopied ? <CheckCircle2 size={14} className="text-green-400" /> : <Copy size={14} />}
                      {refCopied ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>

                  {/* Copiar link */}
                  <button
                  onClick={copiarLinkReferral}
                  className="mt-3 w-full flex items-center justify-center gap-2 text-gray-400 hover:text-white text-xs bg-[#111]/60 border border-[#3a3a3a] rounded-lg py-2.5 transition-colors">
                    <LinkIcon size={13} />
                    {linkCopied ? 'Link de convite copiado!' : 'Copiar link de convite'}
                  </button>

                  {/* Botoes de compartilhamento */}
                  <div className="flex items-center gap-2 mt-4">
                    <button
                    onClick={compartilharInstagram}
                    title="Compartilhar no Instagram (Story)"
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-white text-xs font-semibold bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90 transition-opacity">
                      <InstagramIcon size={15} />
                      Story
                    </button>
                    <button
                    onClick={() => compartilharEm('facebook')}
                    title="Compartilhar no Facebook"
                    className="w-11 h-10 flex items-center justify-center rounded-lg text-white bg-[#1877F2] hover:opacity-90 transition-opacity">
                      <FacebookIcon size={15} />
                    </button>
                    <button
                    onClick={() => compartilharEm('linkedin')}
                    title="Compartilhar no LinkedIn"
                    className="w-11 h-10 flex items-center justify-center rounded-lg text-white bg-[#0A66C2] hover:opacity-90 transition-opacity">
                      <LinkedinIcon size={15} />
                    </button>
                    <button
                    onClick={() => compartilharEm('twitter')}
                    title="Compartilhar no X"
                    className="w-11 h-10 flex items-center justify-center rounded-lg text-white bg-black border border-[#3a3a3a] hover:bg-[#111] transition-colors">
                      <TwitterIcon size={14} />
                    </button>
                  </div>
                </div>
              </div>
            }

            {/* ── MINHA CONTA STRIPE — carteira de recompensas do usuario ── */}
            {isAuthenticated && user?.userId &&
            <div className="flex-1 w-full">
                <SectionHeader icon={<Euro size={15} />} label="Minha Conta Stripe" accentColor="#635BFF" />
                <div className="bg-gradient-to-br from-[#1e1e1e] to-[#242424] border border-[#3a3a3a] rounded-2xl pt-[20px] pl-[20px] pr-[20px] pb-[20px]">
                  {stripeError &&
                <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-3 py-2.5 rounded-lg mb-4">
                      <Info size={14} className="shrink-0" />
                      {stripeError}
                    </div>
                }

                  {!stripeAccountId ? (
                /* ── Sem conta: botao para criar ── */
                <>
                      <p className="text-gray-400 text-sm mb-1 font-semibold">Receba suas recompensas</p>
                      <p className="text-gray-500 text-xs leading-relaxed mb-[102px]">Crie sua conta Stripe para receber pagamentos e recompensas com seguranca, direto do seu perfil.

                  </p>
                      <button
                    onClick={handleCreateStripeAccount}
                    disabled={creatingStripe}
                    className="w-full flex items-center justify-center gap-2 bg-[#635BFF] hover:bg-[#5851e0] text-white text-sm font-bold py-3 rounded-xl transition-all shadow-lg shadow-[#635BFF]/20 disabled:opacity-60 disabled:cursor-not-allowed">
                        {creatingStripe ?
                    <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Criando conta...
                          </> :

                    <>
                            <Plus size={16} />
                            Criar conta e receber recompensas
                          </>
                    }
                      </button>
                    </>) :
                loadingStripe && !stripeData ? (
                /* ── Carregando dados ── */
                <div className="flex items-center justify-center gap-3 py-8 text-gray-500 text-sm">
                      <div className="w-5 h-5 border-2 border-[#635BFF] border-t-transparent rounded-full animate-spin" />
                      Carregando dados da conta...
                    </div>) :
                stripeData ? (
                /* ── Conta existente: dados em tempo real ── */
                <>
                      <div className="flex items-center justify-between mb-4">
                        <span
                      className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border ${
                      stripeData.payoutsEnabled ?
                      'bg-green-500/10 text-green-400 border-green-500/30' :
                      'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'}`
                      }>
                          {stripeData.payoutsEnabled ? <CheckCircle2 size={13} /> : <Clock size={13} />}
                          {stripeData.payoutsEnabled ? 'Conta ativa' : 'Verificacao pendente'}
                        </span>
                        <button
                      onClick={() => loadStripeData(stripeAccountId)}
                      disabled={loadingStripe}
                      title="Atualizar dados"
                      className="text-gray-500 hover:text-white transition-colors disabled:opacity-50">
                          {loadingStripe ?
                      <div className="w-3.5 h-3.5 border-2 border-[#635BFF] border-t-transparent rounded-full animate-spin" /> :

                      <Save size={14} className="rotate-90" />
                      }
                        </button>
                      </div>

                      {/* Saldo disponivel */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-[#111] border border-[#3a3a3a] rounded-xl px-4 py-3">
                          <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Disponivel</p>
                          <p className="text-white font-black text-lg">
                            {(stripeData.available || []).length > 0 ?
                        (stripeData.available || []).map((b: any) =>
                        `${(b.amount / 100).toFixed(2)} ${String(b.currency || '').toUpperCase()}`
                        ).join(' · ') :
                        '0,00 EUR'}
                          </p>
                        </div>
                        <div className="bg-[#111] border border-[#3a3a3a] rounded-xl px-4 py-3">
                          <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Pendente</p>
                          <p className="text-gray-300 font-black text-lg">
                            {(stripeData.pending || []).length > 0 ?
                        (stripeData.pending || []).map((b: any) =>
                        `${(b.amount / 100).toFixed(2)} ${String(b.currency || '').toUpperCase()}`
                        ).join(' · ') :
                        '0,00 EUR'}
                          </p>
                        </div>
                      </div>

                      {/* Informacoes adicionais */}
                      <div className="space-y-2 text-xs">
                        {stripeData.email &&
                    <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1.5 text-gray-500"><Mail size={12} /> E-mail</span>
                            <span className="text-gray-300 truncate max-w-[180px]">{stripeData.email}</span>
                          </div>
                    }
                        {stripeData.country &&
                    <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1.5 text-gray-500"><Globe size={12} /> Pais</span>
                            <span className="text-gray-300">{String(stripeData.country).toUpperCase()}</span>
                          </div>
                    }
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-gray-500"><BadgeCheck size={12} /> Cadastro concluido</span>
                          <span className={stripeData.detailsSubmitted ? 'text-green-400' : 'text-yellow-400'}>
                            {stripeData.detailsSubmitted ? 'Sim' : 'Nao'}
                          </span>
                        </div>
                      </div>

                      {/* Concluir cadastro caso pendente */}
                      {!stripeData.detailsSubmitted &&
                  <button
                    onClick={handleCreateStripeAccount}
                    disabled={creatingStripe}
                    className="mt-4 w-full flex items-center justify-center gap-2 bg-[#635BFF] hover:bg-[#5851e0] text-white text-xs font-bold py-2.5 rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed">
                          {creatingStripe ?
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> :

                    <LinkIcon size={14} />
                    }
                          Concluir cadastro na Stripe
                        </button>
                  }
                    </>) : (

                /* ── Conta existe mas sem dados carregados ── */
                <button
                  onClick={() => loadStripeData(stripeAccountId)}
                  disabled={loadingStripe}
                  className="w-full flex items-center justify-center gap-2 bg-[#635BFF] hover:bg-[#5851e0] text-white text-sm font-bold py-3 rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed">
                      {loadingStripe ?
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> :

                  <Euro size={16} />
                  }
                      Ver dados da minha conta
                    </button>)
                }
                </div>
              </div>
            }
            </div>

            {isEditing &&
          <div className="flex justify-end pt-2">
                <button
              onClick={() => setShowExcluirModal(true)}
              title="Excluir conta"
              className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 hover:text-red-300 hover:border-red-500/40 transition-all px-4 py-2.5 rounded-xl text-sm font-medium">
                  <Trash2 size={15} />
                  Excluir conta
                </button>
              </div>
          }
            {saved &&
          <div className="flex items-center gap-2 text-green-400 text-sm animate-fade-in">
                <CheckCircle2 size={16} /> Perfil salvo com sucesso!
              </div>
          }
          </div>
        }

        {/* TAB: VAGAS (Para Estabelecimentos) */}
        {activeTab === 'vagas' && userProfileType === 'restaurante' &&
        <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 mt-5">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2.5">
                  <div className="w-1 h-5 rounded-full bg-[#C62828]" />
                  <Briefcase size={18} className="text-[#C62828]" />
                  Minhas Vagas
                </h2>
                <p className="text-gray-500 text-xs mt-1 ml-8">Gerencie as vagas criadas pelo seu estabelecimento</p>
              </div>
              <Link
              to="/criar-vaga"
              className="flex items-center gap-2 bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-5 py-2.5 rounded-xl font-medium hover:shadow-lg hover:shadow-[#E53935]/30 transition-all text-sm">
                <Plus size={16} />
                Criar Vaga
              </Link>
            </div>
            
            {loadingVagas ?
          <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-3 border-[#C62828] border-t-transparent rounded-full animate-spin" />
            </div> :
          vagasCriadas.length === 0 ?
          <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-[#3a3a3a] rounded-2xl">
              <div className="w-20 h-20 rounded-full bg-[#333] flex items-center justify-center mb-4">
                <Briefcase size={36} className="text-gray-600" />
              </div>
              <p className="text-gray-400 text-base font-semibold mb-1">Nenhuma vaga publicada</p>
              <p className="text-gray-600 text-sm mb-5 text-center max-w-md">Publique oportunidades de emprego para encontrar profissionais qualificados.</p>
              <Link
              to="/criar-vaga"
              className="flex items-center gap-2 bg-[#C62828] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#b71c1c] transition-all text-sm">
                <Plus size={16} />
                Criar Minha Primeira Vaga
              </Link>
            </div> :
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {vagasCriadas.map((vaga) =>
            <div key={vaga._id} className="bg-[#2a2a2a] border-2 border-[#5D4037] rounded-2xl p-0 hover:border-[#E53935] hover:shadow-2xl hover:shadow-[#E53935]/20 transition-all duration-300 group overflow-hidden w-[70%] mx-auto">
                <div className="h-[240px] w-full bg-[#111] overflow-hidden relative">
                   <img
                  src={BANNER_IMAGES[setor]}
                  alt="Banner do estabelecimento"
                  className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-500" />

                    <div className="absolute inset-0 bg-gradient-to-t from-[#2a2a2a] to-transparent h-1/2 bottom-0 top-auto"></div>
                </div>
                <div className="px-6 pb-6 pt-0 relative flex flex-col items-center">
                  <div className="w-[80px] h-[80px] sm:w-[100px] sm:h-[100px] rounded-full overflow-hidden border-4 border-[#2a2a2a] shadow-[0_4px_12px_rgba(0,0,0,0.5)] ring-2 ring-[#E53935]/40 shrink-0 bg-gradient-to-br from-[#C62828] to-[#E53935] -mt-[40px] sm:-mt-[50px] mb-4 z-10 flex items-center justify-center">
                    <img
                    src={profileImage || "https://static.lumi.new/00/0016247344ae499773930643fb7392de.webp"}
                    alt="Logo do estabelecimento"
                    className="w-full h-full object-cover" />

                  </div>
                  <div className="w-full text-center">
                    <h3 className="text-xl font-bold text-white mb-1 group-hover:text-[#E53935] transition-colors line-clamp-1 capitalize">
                      {vaga.cargo}
                    </h3>
                    <p className="text-lg text-gray-300 font-semibold line-clamp-1 mb-4">{vaga.titulo || displayName}</p>
                  </div>
                  <button
                  onClick={() => handleDeletarVaga(vaga._id)}
                  className="absolute top-4 right-4 w-8 h-8 bg-black/40 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-red-600 transition-all border border-transparent hover:border-red-500 z-10"
                  title="Deletar vaga">
                    <Trash2 size={14} />
                  </button>

                <p className="text-gray-300 w-full mb-4 leading-relaxed line-clamp-2 text-center text-sm">{vaga.descricao}</p>

                <div className="flex flex-wrap gap-2 w-full justify-center mb-4">
                  <div className="flex items-center gap-1.5 text-gray-400 bg-[#1e1e1e] px-2.5 py-1 rounded border border-[#3a3a3a]">
                    <MapPin size={13} className="text-[#E53935]" />
                    <span className="text-[11px] whitespace-nowrap">{vaga.localizacao || "Milão, Itália"}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-400 bg-[#1e1e1e] px-2.5 py-1 rounded border border-[#3a3a3a]">
                    <Clock size={13} className="text-[#E53935]" />
                    <span className="text-[11px] capitalize whitespace-nowrap">{vaga.tipoContrato}</span>
                  </div>
                  {vaga.valorHora &&
                  <div className="flex items-center gap-1.5 text-gray-400 bg-[#1e1e1e] px-2.5 py-1 rounded border border-[#3a3a3a]">
                      <Euro size={13} className="text-[#E53935]" />
                      <span className="text-[11px] font-semibold text-[#E53935] whitespace-nowrap">€{vaga.valorHora}/h</span>
                    </div>
                  }
                  <div className="flex items-center gap-1.5 text-gray-400 bg-[#1e1e1e] px-2.5 py-1 rounded border border-[#3a3a3a]">
                     <span className="text-[11px] capitalize whitespace-nowrap">{vaga.regimeTrabalho?.replace('_', '-')}</span>
                  </div>
                  {vaga.horasSemanais &&
                  <div className="flex items-center gap-1.5 text-gray-400 bg-[#1e1e1e] px-2.5 py-1 rounded border border-[#3a3a3a]">
                       <span className="text-[11px] whitespace-nowrap">{vaga.horasSemanais}h sem</span>
                    </div>
                  }
                </div>
                
                {vaga.detalhesContrato &&
                <div className="border-t border-[#5D4037] pt-4 mb-4 w-full">
                    <p className="text-xs text-gray-400 mb-1.5 font-semibold text-center">Detalhes do Contrato:</p>
                    <p className="text-xs text-gray-300 line-clamp-2 text-center">{vaga.detalhesContrato}</p>
                  </div>
                }

                <div className="flex items-center justify-center border-t border-[#5D4037] pt-4 w-full mt-auto">
                  <span className="text-[11px] text-gray-500">
                    Publicado em {new Date(vaga.createdAt || vaga.dataCriacao || new Date()).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>
              </div>
            )}
            </div>
          }
        </div>
        }

        {/* TAB: CANDIDATOS (Para Estabelecimentos) */}
        {activeTab === 'candidatos' && userProfileType === 'restaurante' &&
        <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 mt-5">
             <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold text-white flex items-center gap-2.5">
                    <div className="w-1 h-5 rounded-full bg-[#E53935]" />
                    <User size={18} className="text-[#E53935]" />
                    Candidatos às suas Vagas
                  </h2>
                  <p className="text-gray-500 text-xs mt-1 ml-8">Veja os profissionais interessados nas suas oportunidades</p>
                </div>
             </div>
             
             {candidatosCadastrados.length === 0 ?
          <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-[#3a3a3a] rounded-2xl">
                 <div className="w-20 h-20 rounded-full bg-[#333] flex items-center justify-center mb-4">
                   <User size={36} className="text-gray-600" />
                 </div>
                 <p className="text-gray-400 text-base font-semibold mb-1">Nenhum candidato ainda</p>
                 <p className="text-gray-600 text-sm mb-5 text-center max-w-md">As inscrições nas suas vagas aparecerão aqui.</p>
               </div> :

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                  {candidatosCadastrados.map((candidato: any) =>
            <div key={candidato._id} className="bg-[#1e1e1e] border border-[#3a3a3a] rounded-2xl overflow-hidden hover:border-[#E53935] hover:shadow-xl transition-all group flex flex-col">
                       <div className="h-[100px] bg-[#111] relative">
                         <img src={candidato.candidatoBanner || mockupCandidatos[0].banner} alt="Banner" className="w-full h-full object-cover opacity-80" />
                         <div className="absolute inset-0 bg-gradient-to-t from-[#1e1e1e] to-transparent"></div>
                       </div>
                       <div className="px-5 pb-5 relative flex-1 flex flex-col items-center text-center">
                          <div className="w-[70px] h-[70px] rounded-full overflow-hidden border-[3px] border-[#1e1e1e] shadow-lg -mt-[35px] mb-3 bg-[#2a2a2a]">
                             <img src={candidato.candidatoFoto || mockupCandidatos[0].foto} alt="Avatar" className="w-full h-full object-cover" />
                          </div>
                          <h3 className="text-white font-bold mb-1 group-hover:text-[#E53935] transition-colors line-clamp-1">{candidato.candidatoNome}</h3>
                          <div className="flex items-center justify-center gap-1.5 text-gray-400 text-xs mb-4 w-full">
                            <Briefcase size={12} className="text-gray-500 shrink-0" />
                            <span className="truncate">{candidato.vagaTitulo || candidato.vagaCargo || "Candidatou-se à vaga"}</span>
                          </div>
                          
                          <div className="mt-auto w-full flex flex-col gap-2 pt-2 border-t border-[#3a3a3a]">
                             <button
                    onClick={() => setCurriculoModalAberto({
                      nome: candidato.candidatoNome,
                      foto: candidato.candidatoFoto || '',
                      cargoCurriculo: candidato.vagaTitulo || candidato.vagaCargo || 'Candidatura',
                      cidade: '',
                      snapshot: candidato.curriculoSnapshot
                    })}
                    className="w-full bg-[#2a2a2a] hover:bg-[#333] text-gray-200 py-2 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 border border-[#444] transition-colors">

                                <FileText size={14} className="text-gray-400" /> Visualizar currículo
                             </button>
                             <button
                    onClick={() => setChatAberto(candidato)}
                    className="w-full bg-gradient-to-r from-[#C62828] to-[#E53935] hover:opacity-90 text-white py-2 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 shadow-md transition-all">

                                <MessageSquare size={14} /> Chat
                             </button>
                          </div>
                       </div>
                    </div>
            )}
               </div>
          }
          </div>
        }

        {/* TAB: MINHAS CANDIDATURAS (Trabalhador) */}
        {activeTab === 'candidaturas' && userProfileType !== 'restaurante' &&
        <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 mt-5">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2.5">
                  <div className="w-1 h-5 rounded-full bg-[#E53935]" />
                  <Briefcase size={18} className="text-[#E53935]" />
                  Minhas Candidaturas
                </h2>
                <p className="text-gray-500 text-xs mt-1 ml-8">Vagas nas quais você se inscreveu</p>
              </div>
              <Link
              to="/vagas"
              className="flex items-center gap-2 bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-5 py-2.5 rounded-xl font-medium hover:shadow-lg hover:shadow-[#E53935]/30 transition-all text-sm">
                <Plus size={16} />
                Ver Vagas
              </Link>
            </div>

            {loadingCandidaturas ?
          <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-[3px] border-[#E53935] border-t-transparent rounded-full animate-spin" />
              </div> :
          minhasCandidaturas.length === 0 ?
          <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-[#3a3a3a] rounded-2xl">
                <div className="w-20 h-20 rounded-full bg-[#333] flex items-center justify-center mb-4">
                  <Briefcase size={36} className="text-gray-600" />
                </div>
                <p className="text-gray-400 text-base font-semibold mb-1">Nenhuma candidatura ainda</p>
                <p className="text-gray-600 text-sm mb-5 text-center max-w-md">Explore as vagas disponíveis e candidate-se às oportunidades que combinam com você.</p>
                <Link
              to="/vagas"
              className="flex items-center gap-2 bg-[#E53935] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#C62828] transition-all text-sm">
                  <Search size={16} />
                  Explorar Vagas
                </Link>
              </div> :

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {minhasCandidaturas.map((cand: any) =>
            <div key={cand._id} className="bg-[#1e1e1e] border border-[#3a3a3a] rounded-2xl overflow-hidden hover:border-[#E53935] hover:shadow-xl transition-all group flex flex-col">
                    {cand.vagaBanner &&
              <div className="h-[90px] bg-[#111] relative overflow-hidden">
                        <img src={cand.vagaBanner} alt="" className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1e1e1e] to-transparent"></div>
                      </div>
              }
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center shrink-0 overflow-hidden">
                          {cand.vagaFoto ?
                    <img src={cand.vagaFoto} alt="" className="w-full h-full object-cover" /> :

                    <Briefcase size={20} className="text-white" />
                    }
                        </div>
                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${
                  cand.status === 'aprovada' ? 'bg-green-500/15 text-green-400 border border-green-500/30' :
                  cand.status === 'recusada' ? 'bg-red-500/15 text-red-400 border border-red-500/30' :
                  'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30'}`
                  }>
                          {cand.status === 'aprovada' ? 'Aprovada' : cand.status === 'recusada' ? 'Recusada' : 'Em análise'}
                        </span>
                      </div>
                      <h3 className="text-white font-bold text-base mb-1 group-hover:text-[#E53935] transition-colors line-clamp-1 capitalize">
                        {cand.vagaCargo || cand.vagaTitulo || 'Vaga'}
                      </h3>
                      <p className="text-gray-400 text-xs mb-2 line-clamp-1">{cand.estabelecimentoNome || cand.vagaTitulo || 'Estabelecimento'}</p>
                      {cand.vagaLocalizacao &&
                <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-3">
                          <MapPin size={11} className="text-[#E53935]" />
                          <span className="truncate">{cand.vagaLocalizacao}</span>
                        </div>
                }

                      <div className="flex items-center gap-2 pt-3 border-t border-[#333] text-gray-500 text-xs mb-3 mt-auto">
                        <Calendar size={11} />
                        <span>
                          {cand.dataCandidatura ?
                    new Date(cand.dataCandidatura).toLocaleDateString('pt-BR') :
                    'Data desconhecida'}
                        </span>
                      </div>

                      <button
                  onClick={() => setChatAberto({
                    candidatoId: cand.estabelecimentoId,
                    candidatoNome: cand.estabelecimentoNome || 'Estabelecimento',
                    candidatoFoto: cand.vagaFoto || '',
                    candidatoBanner: cand.vagaBanner || ''
                  })}
                  disabled={!cand.estabelecimentoId || cand.estabelecimentoId === 'unknown'}
                  className="w-full bg-gradient-to-r from-[#C62828] to-[#E53935] hover:opacity-90 text-white py-2 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 shadow-md transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                        <MessageSquare size={13} /> Conversar com estabelecimento
                      </button>
                    </div>
                  </div>
            )}
              </div>
          }
          </div>
        }

        {/* TAB: CHAT (Trabalhador) */}
        {activeTab === 'chat' &&
        <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 mt-5">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2.5">
                  <div className="w-1 h-5 rounded-full bg-[#E53935]" />
                  <MessageSquare size={18} className="text-[#E53935]" />
                  Minhas Conversas
                </h2>
                <p className="text-gray-500 text-xs mt-1 ml-8">Converse com estabelecimentos e outros usuários</p>
              </div>
            </div>

            {loadingConversas ?
          <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-[3px] border-[#E53935] border-t-transparent rounded-full animate-spin" />
              </div> :
          minhasConversas.length === 0 ?
          <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-[#3a3a3a] rounded-2xl">
                <div className="w-20 h-20 rounded-full bg-[#333] flex items-center justify-center mb-4">
                  <MessageSquare size={36} className="text-gray-600" />
                </div>
                <p className="text-gray-400 text-base font-semibold mb-1">Nenhuma conversa ainda</p>
                <p className="text-gray-600 text-sm mb-5 text-center max-w-md">Quando você iniciar uma conversa com um estabelecimento ou receber uma mensagem, ela aparecerá aqui.</p>
              </div> :

          <div className="space-y-3">
                {minhasConversas.map((conv: any) =>
            <button
              key={conv.conversaId}
              onClick={() => setChatAberto({
                candidatoId: conv.outroUsuarioId,
                candidatoNome: conv.outroNome,
                candidatoFoto: conv.outroFoto,
                candidatoBanner: conv.outroBanner
              })}
              className="w-full flex items-center gap-4 p-4 bg-[#1e1e1e] border border-[#3a3a3a] rounded-2xl hover:border-[#E53935] hover:bg-[#252525] transition-all group text-left">
                    <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#333] bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center shrink-0">
                      {conv.outroFoto ?
                <img src={conv.outroFoto} alt={conv.outroNome} className="w-full h-full object-cover" /> :

                <User size={24} className="text-white" />
                }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="text-white font-bold text-sm truncate group-hover:text-[#E53935] transition-colors">
                          {conv.outroNome}
                        </h3>
                        <span className="text-gray-500 text-[10px] shrink-0">
                          {conv.dataUltimaMensagem ?
                    new Date(conv.dataUltimaMensagem).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) :
                    ''}
                        </span>
                      </div>
                      <p className="text-gray-400 text-xs truncate">{conv.ultimaMensagem}</p>
                    </div>
                    {conv.naoLidas > 0 &&
              <div className="w-6 h-6 rounded-full bg-[#E53935] text-white text-[10px] font-bold flex items-center justify-center shrink-0 shadow-md">
                        {conv.naoLidas}
                      </div>
              }
                  </button>
            )}
              </div>
          }
          </div>
        }

        {/* TAB: AMIGOS */}
        {activeTab === 'amigos' &&
        <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 mt-5">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2.5">
                  <div className="w-1 h-5 rounded-full bg-[#E53935]" />
                  <Users size={18} className="text-[#E53935]" />
                  Meus Amigos
                </h2>
                <p className="text-gray-500 text-xs mt-1 ml-8">Conecte-se com outros profissionais e estabelecimentos</p>
              </div>
            </div>

            {/* Solicitacoes pendentes */}
            {solicitacoesPendentes.length > 0 &&
          <div className="mb-8">
                <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-4">
                  <Bell size={15} className="text-[#E57335]" />
                  Solicitacoes de amizade
                  <span className="w-5 h-5 rounded-full bg-[#E53935] text-white text-[10px] font-bold flex items-center justify-center">{solicitacoesPendentes.length}</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {solicitacoesPendentes.map((sol: any) =>
              <div key={sol._id} className="bg-[#1e1e1e] border border-[#E57335]/30 rounded-2xl p-4 flex items-center gap-3">
                      <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#333] bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center shrink-0">
                        {sol.solicitanteFoto ?
                  <img src={sol.solicitanteFoto} alt={sol.solicitanteNome} className="w-full h-full object-cover" /> :

                  <User size={24} className="text-white" />
                  }
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-bold text-sm truncate">{sol.solicitanteNome || 'Usuario'}</h4>
                        <p className="text-gray-500 text-xs">Quer ser seu amigo</p>
                        <div className="flex gap-2 mt-2">
                          <button
                      onClick={() => aceitarAmizade(sol._id)}
                      className="flex items-center gap-1 bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:opacity-90 transition-all">
                            <UserCheck size={13} /> Aceitar
                          </button>
                          <button
                      onClick={() => recusarAmizade(sol._id)}
                      className="flex items-center gap-1 bg-[#2a2a2a] text-gray-300 px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#444] hover:bg-[#333] transition-all">
                            <XIcon size={13} /> Recusar
                          </button>
                        </div>
                      </div>
                    </div>
              )}
                </div>
              </div>
          }

            {/* Lista de amigos */}
            {loadingAmigos ?
          <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-[3px] border-[#E53935] border-t-transparent rounded-full animate-spin" />
              </div> :
          amigos.length === 0 ?
          <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-[#3a3a3a] rounded-2xl">
                <div className="w-20 h-20 rounded-full bg-[#333] flex items-center justify-center mb-4">
                  <Users size={36} className="text-gray-600" />
                </div>
                <p className="text-gray-400 text-base font-semibold mb-1">Nenhum amigo ainda</p>
                <p className="text-gray-600 text-sm mb-5 text-center max-w-md">Visite o perfil de outros usuarios no Marketplace e clique em "Fazer amizade" para se conectar.</p>
              </div> :

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {amigos.map((amigo: any) =>
            <div key={amigo.amizadeId} className="bg-[#1e1e1e] border border-[#3a3a3a] rounded-2xl overflow-hidden hover:border-[#E53935] hover:shadow-xl transition-all group flex flex-col">
                    <div className="h-[80px] bg-[#111] relative">
                      {amigo.banner ?
                <img src={amigo.banner} alt="" className="w-full h-full object-cover opacity-80" /> :

                <div className="w-full h-full bg-gradient-to-r from-[#3E2723] to-[#6D4C41]" />
                }
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1e1e1e] to-transparent"></div>
                    </div>
                    <div className="px-5 pb-5 relative flex-1 flex flex-col items-center text-center">
                      <div className="w-[70px] h-[70px] rounded-full overflow-hidden border-[3px] border-[#1e1e1e] shadow-lg -mt-[35px] mb-3 bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center">
                        {amigo.foto ?
                  <img src={amigo.foto} alt={amigo.nome} className="w-full h-full object-cover" /> :

                  <User size={28} className="text-white" />
                  }
                      </div>
                      <h3 className="text-white font-bold mb-3 group-hover:text-[#E53935] transition-colors line-clamp-1">{amigo.nome || 'Usuario'}</h3>
                      <div className="mt-auto w-full flex flex-col gap-2 pt-2 border-t border-[#3a3a3a]">
                        <Link
                    to={`/perfil/${encodeURIComponent(amigo.usuarioId)}`}
                    className="w-full bg-[#2a2a2a] hover:bg-[#333] text-gray-200 py-2 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 border border-[#444] transition-colors">
                          <UserRound size={14} className="text-gray-400" /> Ver perfil
                        </Link>
                        <button
                    onClick={() => removerAmizade(amigo.amizadeId)}
                    className="w-full bg-[#2a2a2a] hover:bg-red-600/20 text-gray-400 hover:text-red-400 py-2 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 border border-[#444] hover:border-red-500/40 transition-all">
                          <Trash2 size={13} /> Desfazer amizade
                        </button>
                      </div>
                    </div>
                  </div>
            )}
              </div>
          }
          </div>
        }

        {/* TAB: FEED */}
        {activeTab === 'feed' &&
        <div className="bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-xl p-6 sm:p-9 mt-5">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2.5">
                  <div className="w-1 h-5 rounded-full bg-[#E53935]" />
                  <Rss size={18} className="text-[#E53935]" />
                  Feed
                </h2>
                <p className="text-gray-500 text-xs mt-1 ml-8">Veja as publicacoes dos seus amigos</p>
              </div>
            </div>

            {/* Criar post */}
            <div className="bg-[#1e1e1e] border border-[#3a3a3a] rounded-2xl p-5 mb-6">
              <div className="flex gap-3">
                <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-[#333] bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center shrink-0">
                  {profileImage ?
                <img src={profileImage} alt={displayName} className="w-full h-full object-cover" /> :

                <span className="text-white font-black text-sm">{initials}</span>
                }
                </div>
                <div className="flex-1">
                  <textarea
                  value={novoPostTexto}
                  onChange={(e) => setNovoPostTexto(e.target.value)}
                  rows={3}
                  placeholder="No que voce esta pensando?"
                  className={inputCls + ' resize-none leading-relaxed'} />

                  {novoPostImagens.length > 0 &&
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-3">
                      {novoPostImagens.map((img, idx) =>
                  <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border border-[#3a3a3a]">
                          <img src={img} alt="" className="w-full h-full object-cover" />
                          <button
                      onClick={() => setNovoPostImagens((prev) => prev.filter((_, i) => i !== idx))}
                      className="absolute top-1 right-1 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all hover:bg-red-600">
                            <XIcon size={11} />
                          </button>
                        </div>
                  )}
                    </div>
                }
                  <input type="file" ref={postFotoRef} onChange={handlePostFotoAdd} accept="image/*" multiple className="hidden" />
                  <div className="flex items-center justify-between mt-3">
                    <button
                    onClick={() => postFotoRef.current?.click()}
                    disabled={uploadingPostFoto || novoPostImagens.length >= 4}
                    className="flex items-center gap-2 text-gray-400 hover:text-[#E53935] text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                      {uploadingPostFoto ? <div className="w-4 h-4 border-2 border-[#E53935] border-t-transparent rounded-full animate-spin" /> : <Image size={16} />}
                      Foto
                    </button>
                    <button
                    onClick={publicarPost}
                    disabled={publicandoPost || !novoPostTexto.trim() && novoPostImagens.length === 0}
                    className="flex items-center gap-2 bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-5 py-2 rounded-xl font-semibold text-sm hover:shadow-lg hover:shadow-[#E53935]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                      {publicandoPost ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send size={15} />}
                      Publicar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de posts */}
            {loadingFeed ?
          <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-[3px] border-[#E53935] border-t-transparent rounded-full animate-spin" />
              </div> :
          feedPosts.length === 0 ?
          <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-[#3a3a3a] rounded-2xl">
                <div className="w-20 h-20 rounded-full bg-[#333] flex items-center justify-center mb-4">
                  <Rss size={36} className="text-gray-600" />
                </div>
                <p className="text-gray-400 text-base font-semibold mb-1">Nenhuma publicacao ainda</p>
                <p className="text-gray-600 text-sm text-center max-w-md">Publique algo ou adicione amigos para ver as publicacoes deles aqui.</p>
              </div> :

          <div className="space-y-4 max-w-2xl mx-auto">
                {feedPosts.map((post: any) => {
              const curtidas = Array.isArray(post.curtidas) ? post.curtidas : [];
              const jaCurtiu = curtidas.includes(user?.userId);
              const isMeuPost = post.userId === user?.userId;
              return (
                <div key={post._id} className="bg-[#1e1e1e] border border-[#3a3a3a] rounded-2xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-[#333] bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center shrink-0">
                          {post.userFoto ?
                      <img src={post.userFoto} alt={post.userNome} className="w-full h-full object-cover" /> :

                      <User size={22} className="text-white" />
                      }
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-bold text-sm truncate">{post.userNome || 'Usuario'}</h4>
                          <span className="text-gray-500 text-[11px]">
                            {post.createdAt ? new Date(post.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                        {isMeuPost &&
                    <button
                      onClick={() => deletarPost(post._id)}
                      className="w-8 h-8 rounded-full bg-[#2a2a2a] flex items-center justify-center text-gray-500 hover:text-red-400 hover:bg-red-600/20 transition-all"
                      title="Excluir publicacao">
                            <Trash2 size={14} />
                          </button>
                    }
                      </div>
                      {post.conteudo &&
                  <p className="text-gray-300 text-sm leading-relaxed mb-3 whitespace-pre-wrap">{post.conteudo}</p>
                  }
                      {Array.isArray(post.imagens) && post.imagens.length > 0 &&
                  <div className={`grid gap-2 mb-3 ${post.imagens.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                          {post.imagens.map((img: string, idx: number) =>
                    <img key={idx} src={img} alt="" className="w-full rounded-xl object-cover max-h-72 border border-[#3a3a3a]" />
                    )}
                        </div>
                  }
                      <div className="flex items-center gap-4 pt-3 border-t border-[#333]">
                        <button
                      onClick={() => curtirPost(post)}
                      className={`flex items-center gap-2 text-sm font-semibold transition-colors ${jaCurtiu ? 'text-[#E53935]' : 'text-gray-400 hover:text-[#E53935]'}`}>
                          <Heart size={16} className={jaCurtiu ? 'fill-[#E53935]' : ''} />
                          {curtidas.length > 0 ? curtidas.length : ''} Curtir
                        </button>
                      </div>
                    </div>);

            })}
              </div>
          }
          </div>
        }

        {/* TAB: CURRICULO */}
        {activeTab === 'curriculo' && userProfileType !== 'restaurante' && <>
          <div className="my-4 rounded-xl bg-[#222] p-4 text-sm text-gray-300" role="status">
            {isDemoMode ? 'Currículo fictício salvo somente nesta demonstração. Ele será apagado ao reiniciar os testes ou fechar esta aba.' : cvCloud ? 'Seu currículo é privado e fica salvo na sua conta.' : 'Seu currículo fica salvo apenas neste navegador, separado por conta. Ele não é enviado aos estabelecimentos. Use seu dispositivo pessoal e exporte o PDF para guardar uma cópia.'}
            {!cvCloud && <button className="block mt-2 underline" onClick={() => {
              if (!user?.userId || !window.confirm('Apagar somente o currículo salvo neste navegador?')) return;
              try { createCurriculoStore({ cloud: false, storage: window.localStorage, entity: null }).removeLocal(user.userId); setCvReload(n => n + 1); }
              catch { setCvError('Não foi possível apagar a cópia local.'); }
            }}>Apagar cópia deste navegador</button>}
          </div>
          {cvError && <p role="alert" className="text-red-300 mb-4">{cvError} {cvLoadFailed && <button className="underline" onClick={() => setCvReload(n => n + 1)}>Tentar novamente</button>}</p>}
          {cvLoading ? <p className="text-gray-300">Carregando currículo...</p> : !cvLoadFailed && (curriculoSalvo ? curriculoViewContent : curriculoEditContent)}
        </>}
      </div>

        {/* LIGHTBOX DO ÁLBUM */}
        {fotoExpandida !== null && albumPhotos[fotoExpandida] &&
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 backdrop-blur-sm"
             onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEndHandler}
             onClick={() => setFotoExpandida(null)}>
            <button onClick={() => setFotoExpandida(null)} className="absolute top-6 right-6 w-10 h-10 bg-[#222] rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors z-50">
              <XIcon size={20} />
            </button>

            {/* Setas (Apenas em desktop/telas maiores) */}
            {albumPhotos.length > 1 &&
            <>
                <button onClick={(e) => { e.stopPropagation(); prevFoto(); }} className="hidden sm:flex absolute left-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-[#222] rounded-full items-center justify-center text-white hover:bg-[#E53935] transition-colors z-50 shadow-lg">
                  <span className="text-2xl font-bold -ml-1">‹</span>
                </button>
                <button onClick={(e) => { e.stopPropagation(); nextFoto(); }} className="hidden sm:flex absolute right-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-[#222] rounded-full items-center justify-center text-white hover:bg-[#E53935] transition-colors z-50 shadow-lg">
                  <span className="text-2xl font-bold ml-1">›</span>
                </button>
              </>
            }

            {/* Imagem */}
            <div className="relative max-w-full max-h-[90vh] px-4 sm:px-12 flex items-center justify-center pointer-events-none" onClick={(e) => e.stopPropagation()}>
              <img src={albumPhotos[fotoExpandida]} alt={`Foto Expandida ${fotoExpandida + 1}`} className="max-w-full max-h-[85vh] object-contain rounded-lg pointer-events-auto shadow-2xl border border-[#333]" />
              <div className="absolute bottom-[-30px] left-1/2 -translate-x-1/2 text-white/50 text-sm tracking-widest font-semibold pointer-events-auto select-none bg-black/40 px-3 py-1 rounded-full backdrop-blur-md">
                {fotoExpandida + 1} / {albumPhotos.length}
              </div>
            </div>
          </div>
        }

      {/* CHAT LATERAL */}
      {chatAberto &&
      <div className="fixed bottom-16 right-4 sm:right-8 w-[calc(100vw-2rem)] sm:w-[360px] max-h-[calc(100dvh-6rem)] bg-[#1e1e1e] border border-[#3a3a3a] rounded-xl shadow-[0_-5px_25px_rgba(0,0,0,0.5)] z-[100] flex flex-col overflow-hidden animate-fade-in">
          {/* Header do Chat */}
          <div className="bg-[#2a2a2a] border-b border-[#3a3a3a] p-3 flex shrink-0 items-center justify-between">
            <div className="flex items-center gap-2">
               <div className="w-8 h-8 rounded-full overflow-hidden border border-[#555]">
                 <img src={chatAberto.candidatoFoto || mockupCandidatos[0].foto} alt="Avatar" className="w-full h-full object-cover" />
               </div>
               <div>
                  <h4 className="text-white text-sm font-bold truncate max-w-[150px]">{chatAberto.candidatoNome}</h4>
                  <span className="text-green-400 text-[10px] flex items-center gap-1"><div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>Online</span>
               </div>
            </div>
            <button onClick={() => setChatAberto(null)} className="text-gray-400 hover:text-white transition-colors">
              <XIcon size={16} />
            </button>
          </div>
          
          {/* Corpo do Chat */}
          <div className="flex-1 min-h-0 h-[250px] overflow-y-auto bg-[#1a1a1a] p-3 space-y-3 flex flex-col">
             {loadingChat && mensagensChat.length === 0 ?
          <div className="flex-1 flex items-center justify-center">
                 <div className="w-6 h-6 border-2 border-[#E53935] border-t-transparent rounded-full animate-spin"></div>
               </div> :
          mensagensChat.length === 0 ?
          <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
                 <div className="w-12 h-12 bg-[#333] rounded-full flex items-center justify-center mb-2">
                   <MessageSquare size={20} className="text-gray-500" />
                 </div>
                 <p className="text-gray-500 text-xs">Mande uma mensagem para iniciar a conversa.</p>
               </div> :

          mensagensChat.map((msg, idx) => {
            const isMe = msg.remetenteId === user?.userId;
            return (
              <div key={msg._id || idx} className={`flex gap-2 ${isMe ? 'justify-end' : ''}`}>
                     {!isMe &&
                <img src={chatAberto.candidatoFoto || mockupCandidatos[0].foto} className="w-6 h-6 rounded-full border border-[#333]" />
                }
                     <div className={`text-xs px-3 py-2 rounded-xl max-w-[80%] leading-relaxed ${
                isMe ?
                'bg-[#E53935] text-white rounded-tr-none shadow-md' :
                'bg-[#2a2a2a] text-gray-300 rounded-tl-none border border-[#3a3a3a]'}`
                }>
                       {msg.texto}
                     </div>
                   </div>);

          })
          }
          </div>
          
          {/* Input do Chat */}
          <div className="bg-[#2a2a2a] border-t border-[#3a3a3a] p-3 flex shrink-0 gap-2">
            <input
            type="text"
            value={chatMensagem}
            onChange={(e) => setChatMensagem(e.target.value)}
            placeholder="Digite uma mensagem..."
            className="flex-1 min-w-0 bg-[#111] border border-[#3a3a3a] rounded-full px-3 text-xs text-white placeholder-gray-500 outline-none focus:border-[#E53935]"
            onKeyDown={(e) => {if (e.key === 'Enter') {enviarMensagemChat();}}} />

            <button
            onClick={enviarMensagemChat}
            disabled={!chatMensagem.trim()}
            className="w-8 h-8 rounded-full bg-[#E53935] text-white flex items-center justify-center shrink-0 hover:bg-[#C62828] transition-colors disabled:opacity-50">
              <span className="text-[10px] font-bold">Enviar</span>
            </button>
          </div>
        </div>
      }

      {/* MODAL CURRICULO CANDIDATO */}
      {curriculoModalAberto &&
      <div className="fixed inset-0 z-[150] flex items-center justify-center p-4" onClick={() => setCurriculoModalAberto(null)}>
           <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
           <div className="relative bg-[#111] border border-[#3a3a3a] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 z-20 bg-[#1a1a1a] border-b border-[#3a3a3a] px-6 py-4 flex items-center justify-between">
                 <h2 className="text-white font-bold">Currículo de {curriculoModalAberto.nome}</h2>
                 <button onClick={() => setCurriculoModalAberto(null)} className="text-gray-400 hover:text-white bg-[#333] hover:bg-[#444] rounded-full w-8 h-8 flex items-center justify-center transition-colors">
                   <XIcon size={16} />
                 </button>
              </div>
              <div className="p-6">
                 {/* Reutilizando a estrutura de exibição visual do currículo de forma simplificada para o candidato */}
                 <div className="flex flex-col sm:flex-row gap-6 mb-8 items-center sm:items-start text-center sm:text-left">
                    {curriculoModalAberto.foto && <img src={curriculoModalAberto.foto} alt="Foto" className="w-24 h-24 rounded-full object-cover border-4 border-[#333]" />}
                    <div>
                       <h1 className="text-3xl font-black text-white">{curriculoModalAberto.nome}</h1>
                       <p className="text-[#E57335] font-semibold mt-1">{curriculoModalAberto.cargoCurriculo}</p>
                       <div className="flex items-center justify-center sm:justify-start gap-2 mt-2 text-gray-500 text-sm">
                         <MapPin size={14} className="text-[#E53935]" /> {curriculoModalAberto.cidade}
                       </div>
                    </div>
                 </div>
                 <CandidaturaCurriculo snapshot={curriculoModalAberto.snapshot} />

              </div>
           </div>
         </div>
      }

      {/* MODAL: STORY DO INSTAGRAM (REFERRAL) */}
      {showStoryModal &&
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={() => setShowStoryModal(false)}>
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />
          <div className="relative bg-[#1a1a1a] border border-[#3a3a3a] rounded-2xl shadow-2xl w-full max-w-sm max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#2a2a2a]">
              <h2 className="text-white font-bold text-base flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF]">
                  <InstagramIcon size={16} />
                </span>
                Story do Instagram
              </h2>
              <button
              onClick={() => setShowStoryModal(false)}
              className="w-8 h-8 rounded-full bg-[#2a2a2a] flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#3a3a3a] transition-all">
                <XIcon size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-5">
              <p className="text-gray-400 text-xs mb-4 leading-relaxed">
                Baixe a imagem abaixo, publique no seu Story do Instagram e cole o seu codigo de indicacao. O link de convite ja foi copiado para a area de transferencia!
              </p>

              {/* Preview do story com overlay */}
              <div className="relative w-full aspect-[9/16] rounded-2xl overflow-hidden border border-[#3a3a3a] shadow-lg bg-[#111]">
                <img src={REFERRAL_STORY_BG} alt="Story de indicacao BBQ Jobs" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70" />
                <div className="absolute inset-0 flex flex-col items-center justify-between px-5 py-8 text-center">
                  <div>
                    <p className="text-white/90 text-xs font-semibold tracking-[0.25em] uppercase">BBQ Jobs</p>
                    <h3 className="text-white text-2xl font-black leading-tight mt-2 drop-shadow-lg">Gastronomia brasileira pelo mundo</h3>
                  </div>
                  <div className="w-full">
                    <p className="text-white/80 text-[10px] uppercase tracking-widest mb-2">Codigo de indicacao</p>
                    <div className="inline-block bg-black/60 backdrop-blur-md border border-white/30 rounded-xl px-5 py-3">
                      <span className="text-white text-2xl font-black tracking-[0.2em]">{referralCode}</span>
                    </div>
                    <p className="text-white/70 text-[10px] mt-3 break-all">{referralLink}</p>
                  </div>
                </div>
              </div>

              {/* Codigo + copiar */}
              <div className="flex items-center justify-between bg-[#111] border border-dashed border-[#E53935]/40 rounded-xl px-4 py-3 mt-4">
                <span className="text-[#E57335] font-black tracking-[0.2em] text-lg select-all">{referralCode}</span>
                <button
                onClick={copiarReferral}
                title="Copiar codigo"
                className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors text-xs font-medium">
                  {refCopied ? <CheckCircle2 size={14} className="text-green-400" /> : <Copy size={14} />}
                  {refCopied ? 'Copiado' : 'Copiar'}
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 px-5 py-4 border-t border-[#2a2a2a]">
              <button
              onClick={() => setShowStoryModal(false)}
              className="flex-1 flex items-center justify-center gap-2 bg-[#2a2a2a] text-gray-300 px-4 py-2.5 rounded-xl font-medium hover:bg-[#333] transition-all text-sm border border-[#3a3a3a]">
                Fechar
              </button>
              <button
              onClick={baixarStoryImagem}
              className="flex-1 flex items-center justify-center gap-2 text-white px-4 py-2.5 rounded-xl font-bold text-sm bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90 transition-opacity shadow-lg">
                <Download size={16} />
                Baixar imagem
              </button>
            </div>
          </div>
        </div>
      }

      <Footer />
    </div>);

}

export default Perfil;